// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Invoices CRUD
// Contractor billing with auto-calculation from tickets + rates
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Table, Divider,
  Alert, NumberInput, Collapse, Checkbox,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconCalculator, IconReceipt, IconFileTypePdf, IconScale,
  IconClipboardCheck, IconCopy, IconChevronDown, IconChevronUp,
  IconAlertTriangle, IconRobot, IconCash, IconLoader2,
  IconEye, IconBuilding, IconMapPin, IconFileText,
  IconTool, IconPackage, IconShieldCheck, IconExternalLink,
  IconFlag, IconCheck, IconX, IconGitMerge, IconLink,
  IconUpload, IconDownload,
} from '@tabler/icons-react';
import { analyzeInvoice } from '../../lib/invoiceAI';
import type { InvoiceAnalysis } from '../../lib/invoiceAI';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { notifications } from '../../lib/notifications';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useAllTickets } from '../../hooks/useAllTickets';
import { useChecklistItems } from '../../hooks/useChecklistItems';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fetchTable, TABLES, FIELDS } from '../../lib/api-switch';
import { fetchRecords as apiFetchRecords, queryRecords, createRecord, updateRecord as apiUpdateRecord } from '../../api/client';
import { uploadFileToV2, getV2FileDownloadUrl } from '../../lib/uploadFile';
import { haversineDistance } from '../../lib/geoopsGeo';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisInvoice, DebrisType, TampaInvoiceStatus, InvoiceQualityScore } from '../../types/debris';
import { INVOICE_TYPE_LABELS } from '../../types/checklist';
import type { InvoiceType, ChecklistTemplate } from '../../types/checklist';
import { DEFAULT_TEMPLATES } from '../../data/checklistDefaults';
import { computeDebrisAutoChecks, computeNonDebrisAutoChecks } from '../../utils/checklistAutoChecks';
import { useNavigate } from 'react-router-dom';
import { logActivity } from '../../lib/auditLog';
import { parseInvoiceSpreadsheet, generateInvoiceTemplate, downloadWorkbook } from '../../lib/bulkImport';
import type { InvoiceRow } from '../../lib/bulkImport';

// Map debris type → primary rate schedule line item code for hauling
const TYPE_TO_LINE_ITEM: Record<string, string> = {
  vegetative: '1', c_and_d: '1', soil_mud_sand: '1',
  hazardous_tree: '18', stump: '14',
  white_goods: '12', e_waste: '12',  // map to Freon/per-UNIT line item (not per-LB)
  hhw: '11',
  mattresses: '1', tires: '1', transformers: '1',
  vehicles: '26', vessels: '26', mixed: '1',
};

// Unit-based debris types are counted per item on tickets but the FEMA rate schedule
// may price them per LB. When the rate schedule unit doesn't match the ticket measurement,
// use these per-unit fallback rates (reasonable per-appliance/item costs).
const UNIT_RATE_OVERRIDE: Record<string, number> = {
  white_goods: 75.00,   // per appliance (avg fridge/washer/dryer pickup + haul + disposal)
  e_waste: 25.00,       // per e-waste item
};

// Distance tier thresholds in km (converted from miles)
const TIER_THRESHOLDS = [24.14, 48.28, 96.56]; // 15mi, 30mi, 60mi

function getDistanceTier(pickupLat: number, pickupLng: number, siteLat: number, siteLng: number): number {
  if (!pickupLat || !pickupLng || !siteLat || !siteLng) return 0;
  const distKm = haversineDistance(pickupLat, pickupLng, siteLat, siteLng) / 1000;
  if (distKm <= TIER_THRESHOLDS[0]) return 0; // tier 1
  if (distKm <= TIER_THRESHOLDS[1]) return 1; // tier 2
  if (distKm <= TIER_THRESHOLDS[2]) return 2; // tier 3
  return 3; // tier 4
}

interface RateRow { lineItemCode: string; unit: string; baseRate: string; tier2Rate: string; tier3Rate: string; tier4Rate: string; description: string; contractorId: string; incidentId: string; }

function getRateForTier(row: RateRow, tier: number): number {
  const rates = [row.baseRate, row.tier2Rate, row.tier3Rate, row.tier4Rate];
  return parseFloat(rates[tier] || rates[0] || '0') || 0;
}

interface LineItem {
  debrisType: string;
  label: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  ticketCount: number;
  tier: string;
  lineItemCode: string;
}

interface ManualLineItem {
  id: string;
  description: string;
  quantity: string;
  unit: string;
  rate: string;
  amount: string; // may be manually overridden (not just qty×rate)
  supportingDocId?: string;
}

const UNIT_OPTIONS = [
  { value: 'CY', label: 'CY (Cubic Yards)' },
  { value: 'TN', label: 'TN (Tons)' },
  { value: 'TON', label: 'TON' },
  { value: 'LS', label: 'LS (Lump Sum)' },
  { value: 'EA', label: 'EA (Each)' },
  { value: 'HR', label: 'HR (Hours)' },
  { value: 'LF', label: 'LF (Linear Feet)' },
  { value: 'SF', label: 'SF (Square Feet)' },
  { value: 'DAY', label: 'DAY' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'reconciled', label: 'Reconciled' },
  { value: 'approved', label: 'Approved' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'consolidated', label: 'Consolidated' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', reconciled: '#3b82f6',
  approved: 'var(--color-success)', payment_pending: '#C68700', paid: '#03AC9E', disputed: 'var(--color-error)',
  consolidated: '#6366f1',
};

function getPaymentStatus(inv: DebrisInvoice, getPayments: (inv: DebrisInvoice) => any[]): 'paid' | 'partial' | 'unpaid' {
  const payments = getPayments(inv);
  if (!payments.length) return 'unpaid';
  const totalPaid = payments.reduce((s: number, p: any) => s + (p.amount || 0), 0);
  const invoiced = parseFloat((inv as any).totalAmount || '0');
  if (invoiced > 0 && totalPaid >= invoiced * 0.99) return 'paid';
  if (totalPaid > 0) return 'partial';
  return 'unpaid';
}

const PAYMENT_STATUS_COLORS = {
  paid:    { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: 'PAID' },
  partial: { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: 'PARTIAL' },
  unpaid:  { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', label: 'UNPAID' },
};

// ── Tampa Invoice Meta: three-party amounts ───────────────────
// Extends the existing |||IMETA||| pattern in reviewerNotes
const INVOICE_META_RE = /\|\|\|IMETA\|\|\|([\s\S]*?)\|\|\|ENDIMETA\|\|\|/;

interface TampaInvoiceMeta {
  totalCubicYards?: string;
  ticketIds?: string;
  approvedBy?: string;
  // Tampa three-party fields
  reconciliationId?: string;
  contractorSubmittedAmount?: number;
  monitorVerifiedAmount?: number;
  paymentRef?: string;
  // Consolidation fields
  sourceInvoices?: number[];      // IDs of invoices merged into this one (on parent)
  consolidatedInto?: number;       // ID of parent consolidated invoice (on source)
  supportingDocIds?: string[];    // Evidence Archive document record_ids
  qualityChecks?: Record<string, boolean>;
}

const TAMPA_META_RE = /\|\|\|TAMPA\|\|\|([\s\S]*?)\|\|\|ENDTAMPA\|\|\|/;

// Full AI-extracted invoice data from Package Processor
interface TampaAIExtraction {
  vendor?: string;
  vendorAddress?: string;
  poNumber?: string;
  disasterReference?: string;
  jobDescription?: string;
  jobLocation?: string;
  laborBreakdown?: { classification: string; hours: number; rate: number; amount: number }[];
  materials?: { description: string; supplierInvoice?: string; amount: number }[];
  qualityScore?: string;
  missingItems?: string[];
  aiSummary?: string;
  hasItemizedMaterials?: boolean;
  hasLaborBreakdown?: boolean;
  hasDailyServiceTickets?: boolean;
  qualityChecks?: Record<string, boolean>;
  // Three-party fields may also be here
  contractorSubmittedAmount?: number;
  monitorVerifiedAmount?: number;
  paymentRef?: string;
}

function decodeTampaMeta(notes: string): TampaInvoiceMeta {
  const safe = notes ?? '';
  let meta: TampaInvoiceMeta = {};
  // Check IMETA block first
  const im = safe.match(INVOICE_META_RE);
  if (im) { try { meta = { ...meta, ...JSON.parse(im[1]) }; } catch { /* */ } }
  // Check TAMPA block (our custom marker)
  const tm = safe.match(TAMPA_META_RE);
  if (tm) { try { meta = { ...meta, ...JSON.parse(tm[1]) }; } catch { /* */ } }
  return meta;
}

function decodeFullTampaMeta(notes: string): TampaAIExtraction {
  const safe = notes ?? '';
  const tm = safe.match(TAMPA_META_RE);
  if (!tm) return {};
  try { return JSON.parse(tm[1]); } catch { return {}; }
}

const fmtDate = (d: string) => {
  if (!d) return '-';
  const s = d.slice(0, 10);
  if (s.includes('-')) {
    const [y, m, day] = s.split('-');
    return `${m}/${day}/${y}`;
  }
  return s;
};

/** Normalize any date string to YYYY-MM-DD for range comparisons */
const toISODate = (d: string): string => {
  if (!d) return '';
  // Already ISO: "2024-12-30" or "2024-12-30T..."
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  // MM/DD/YYYY
  const m = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return d.slice(0, 10);
};

const EMPTY: Partial<DebrisInvoice> = {
  contractorId: '', invoiceNumber: '', periodStart: '', periodEnd: '',
  totalCubicYards: '', totalAmount: '', ticketIds: '[]', lineItems: '[]',
  status: 'draft', approvedBy: '', notes: '',
};

// ═══════════════════════════════════════════════════════════════
// InvoiceDocumentationPanel — Documentation checklist tab
// ═══════════════════════════════════════════════════════════════
interface InvoiceDocumentationPanelProps {
  inv: DebrisInvoice;
  invoiceType: InvoiceType | null;
  checklistTemplate: ChecklistTemplate | null;
  checklistTemplateLoading: boolean;
  checklistItems: import('../../types/checklist').ChecklistItem[];
  checklistItemsLoading: boolean;
  checklistWaiverOpen: string | null;
  checklistWaiverText: string;
  setChecklistWaiverOpen: (id: string | null) => void;
  setChecklistWaiverText: (v: string) => void;
  checklistUploading: string | null;
  setChecklistUploading: (id: string | null) => void;
  saveItem: (
    templateItemId: string,
    label: string,
    evidenceType: 'file' | 'manual',
    status: import('../../types/checklist').ChecklistItemStatus,
    extra?: { fileDocId?: string; fileName?: string; waiverReason?: string; notes?: string; checkedBy?: string },
  ) => Promise<void>;
  loadTemplate: (t: InvoiceType) => Promise<void>;
  updateInvoice: (id: number, data: Partial<DebrisInvoice>) => Promise<void>;
  linkedTickets: import('../../types/debris').DebrisLoadTicket[];
  trucks: import('../../types/debris').DebrisTruck[];
  canEdit: boolean;
}

function InvoiceDocumentationPanel({
  inv,
  invoiceType,
  checklistTemplate,
  checklistTemplateLoading,
  checklistItems,
  checklistItemsLoading,
  checklistWaiverOpen,
  checklistWaiverText,
  setChecklistWaiverOpen,
  setChecklistWaiverText,
  checklistUploading,
  setChecklistUploading,
  saveItem,
  loadTemplate,
  updateInvoice,
  linkedTickets,
  trucks,
  canEdit,
}: InvoiceDocumentationPanelProps) {
  // Compute auto-check results
  const autoResults = (() => {
    if (!invoiceType) return [];
    if (invoiceType === 'debris') return computeDebrisAutoChecks(inv, linkedTickets, trucks);
    if (invoiceType === 'non_debris_contractor') return computeNonDebrisAutoChecks(inv, checklistItems);
    return [];
  })();

  const template = checklistTemplate ?? (invoiceType ? DEFAULT_TEMPLATES.find(t => t.invoiceType === invoiceType) ?? null : null);
  const templateItems = template?.items ?? [];

  // Split by evidenceType
  const autoItems = templateItems.filter(i => i.evidenceType === 'auto').sort((a, b) => a.order - b.order);
  const fileItems = templateItems.filter(i => i.evidenceType === 'file').sort((a, b) => a.order - b.order);
  const manualItems = templateItems.filter(i => i.evidenceType === 'manual').sort((a, b) => a.order - b.order);

  // Helpers (must be defined before progress calculation)
  const getItemRecord = (templateItemId: string) =>
    checklistItems.find(c => c.templateItemId === templateItemId);

  // Manual override: check if a checklist item exists for an auto-check (manual override)
  const getOverride = (templateItemId: string) => {
    const ci = checklistItems.find(c => c.templateItemId === templateItemId && c.evidenceType === 'manual');
    return ci?.status === 'satisfied' ? ci : null;
  };

  // Progress: ALL required items (auto + file + manual)
  const requiredItems = templateItems.filter(i => i.required);
  const completedItems = requiredItems.filter(i => {
    if (i.evidenceType === 'auto') {
      const result = autoResults.find(r => r.templateItemId === i.id);
      const override = getOverride(i.id);
      return (result?.status === 'pass') || !!override;
    }
    const ci = checklistItems.find(c => c.templateItemId === i.id);
    return ci && (ci.status === 'satisfied' || ci.status === 'waived');
  });
  const progressPct = requiredItems.length > 0 ? (completedItems.length / requiredItems.length) * 100 : 100;

  const handleAutoOverride = async (item: { id: string; label: string }, autoStatus: 'pass' | 'fail' | 'unavailable') => {
    if (!canEdit) return;
    const existing = getOverride(item.id);
    if (existing) {
      // Revert: set back to pending (removes override)
      await saveItem(item.id, item.label, 'manual', 'pending', { notes: 'Override reverted' });
    } else if (autoStatus === 'fail' || autoStatus === 'unavailable') {
      // Override fail → pass
      await saveItem(item.id, item.label, 'manual', 'satisfied', { notes: 'Manual override — confirmed by reviewer' });
    }
  };

  const statusIcon = (status: 'pass' | 'fail' | 'unavailable') => {
    if (status === 'pass') return <span style={{ color: '#037730', fontWeight: 700, fontSize: 14 }}>✓</span>;
    if (status === 'fail') return <span style={{ color: '#D33423', fontWeight: 700, fontSize: 14 }}>✗</span>;
    return <span style={{ color: '#94a3b8', fontSize: 14 }}>—</span>;
  };

  const statusBadgeStyle = (status: 'pass' | 'fail' | 'unavailable', overridden?: boolean) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
    background: overridden ? '#dbeafe' : status === 'pass' ? '#dcfce7' : status === 'fail' ? '#fee2e2' : '#f1f5f9',
    color: overridden ? '#1e40af' : status === 'pass' ? '#166534' : status === 'fail' ? '#991b1b' : '#64748b',
    border: `1px solid ${overridden ? '#93c5fd' : status === 'pass' ? '#bbf7d0' : status === 'fail' ? '#fecaca' : '#e2e8f0'}`,
    cursor: canEdit ? 'pointer' : undefined,
  });

  const itemRowStyle = {
    padding: '12px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 12, alignItems: 'flex-start',
  };

  // Invoice type selector — save directly via updateRecord (not through the store's complex updateInvoice)
  const [savingType, setSavingType] = useState(false);
  const handleSetInvoiceType = async (newType: InvoiceType) => {
    setSavingType(true);
    try {
      await apiUpdateRecord(TABLES.debrisInvoices, inv.id, {
        [FIELDS.debrisInvoices.invoiceType]: newType,
      });
      await useDebrisOpsStore.getState().loadInvoices(); // reload so inv.invoiceType updates in parent
      await loadTemplate(newType);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to set invoice type', color: 'var(--color-error)' });
    } finally {
      setSavingType(false);
    }
  };

  // File upload handler — creates a debrisDocuments record and properly confirms the
  // S3 upload by writing attachmentId to the record's ATTACHMENT field. This is required
  // for GAB to serve the download URL (otherwise 409 Conflict).
  const handleFileUpload = async (templateItemId: string, label: string, file: File) => {
    setChecklistUploading(templateItemId);
    try {
      const FDoc = FIELDS.debrisDocuments;
      // 1. Create a debrisDocuments record for this file
      const docRec = await createRecord<Record<string, unknown>>(TABLES.debrisDocuments, {
        [FDoc.incidentId]: String(inv.incidentId || ''),
        [FDoc.title]: `${label} — ${file.name}`,
        [FDoc.documentType]: 'checklist_evidence',
        [FDoc.uploadedBy]: 'checklist-upload',
        [FDoc.notes]: `|||INVOICELINK|||{"invoiceId":"${inv.id}"}|||ENDINVOICELINK|||`,
      });
      const recId = (docRec as any).record_id;
      // 2. Upload the file and get attachmentId + documentId
      const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.debrisDocuments);
      // 3. Confirm the upload by writing attachmentId to the record's ATTACHMENT field
      if (recId) {
        await apiUpdateRecord(TABLES.debrisDocuments, recId, {
          [FDoc.file]: attachmentId,
          [FDoc.notes]: `|||INVOICELINK|||{"invoiceId":"${inv.id}"}|||ENDINVOICELINK|||\n|||DOCID|||${documentId}|||ATTACHID|||${attachmentId}|||`,
        });
      }
      // 4. Store the documentId in the checklist item for download URL retrieval
      await saveItem(templateItemId, label, 'file', 'satisfied', {
        fileDocId: documentId,
        fileName: file.name,
      });
      notifications.show({ message: `${file.name} uploaded`, color: 'var(--color-success)' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Upload failed', color: 'var(--color-error)' });
    } finally {
      setChecklistUploading(null);
    }
  };

  const handleViewDoc = async (documentId: string) => {
    try {
      const url = await getV2FileDownloadUrl(documentId);
      window.open(url, '_blank');
    } catch {
      notifications.show({ message: 'Could not retrieve document URL', color: 'var(--color-error)' });
    }
  };

  // Load template on mount if invoiceType is set but template isn't loaded yet
  useEffect(() => {
    if (invoiceType && !checklistTemplate && !checklistTemplateLoading) {
      loadTemplate(invoiceType);
    }
  }, [invoiceType, checklistTemplate, checklistTemplateLoading, loadTemplate]);

  return (
    <div>
      {/* Invoice Type Selector */}
      <div style={{ marginBottom: 20 }}>
        {!invoiceType ? (
          <div style={{ padding: '16px', border: '1px dashed #c7d2fe', borderRadius: 10, background: '#fafbff' }}>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>Set Invoice Type to enable documentation checklist</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(Object.entries(INVOICE_TYPE_LABELS) as [InvoiceType, string][]).map(([k, v]) => (
                <button key={k} onClick={() => handleSetInvoiceType(k)}
                  disabled={savingType}
                  style={{
                    padding: '7px 16px', borderRadius: 8, border: '1px solid #c7d2fe',
                    background: '#fff', color: '#4B3FFF', cursor: 'pointer',
                    fontSize: 13, fontWeight: 500, opacity: savingType ? 0.6 : 1,
                  }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              padding: '4px 12px', borderRadius: 8, background: '#ede9fe', color: '#4B3FFF',
              fontWeight: 700, fontSize: 13, border: '1px solid #c4b5fd',
            }}>
              {INVOICE_TYPE_LABELS[invoiceType] ?? invoiceType}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {(Object.entries(INVOICE_TYPE_LABELS) as [InvoiceType, string][]).filter(([k]) => k !== invoiceType).map(([k, v]) => (
                <button key={k} onClick={() => handleSetInvoiceType(k)}
                  disabled={savingType}
                  style={{
                    padding: '3px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
                    background: '#f8fafc', color: '#64748b', cursor: 'pointer',
                    fontSize: 11, opacity: savingType ? 0.6 : 1,
                  }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Checklist Panel */}
      {invoiceType && (
        <>
          {/* Progress bar */}
          {requiredItems.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>Required Items Complete</span>
                <span style={{ fontSize: 12, color: progressPct === 100 ? '#037730' : '#64748b', fontWeight: 600 }}>
                  {completedItems.length} / {requiredItems.length}
                </span>
              </div>
              <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  background: progressPct === 100 ? '#037730' : progressPct >= 50 ? '#4B3FFF' : '#f59e0b',
                  width: `${progressPct}%`, transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}

          {checklistTemplateLoading || checklistItemsLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading checklist...</div>
          ) : templateItems.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No checklist template found for this invoice type.</div>
          ) : (
            <div>
              {/* AUTO CHECKS */}
              {autoItems.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Automated Checks
                    </div>
                    {canEdit && (
                      <div style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
                        Double-click a failed item to manually override
                      </div>
                    )}
                  </div>
                  {autoItems.map(item => {
                    const result = autoResults.find(r => r.templateItemId === item.id);
                    const autoStatus = result?.status ?? 'unavailable';
                    const override = getOverride(item.id);
                    const effectiveStatus = override ? 'pass' : autoStatus;
                    const isOverridden = !!override;
                    const isUploading = checklistUploading === item.id;
                    // Show upload button for items that look like they need a file (SOW, inspection, etc.)
                    const needsFile = (autoStatus === 'fail' || autoStatus === 'unavailable') && !isOverridden;
                    return (
                      <div key={item.id} style={itemRowStyle}>
                        <div style={{ width: 22, flexShrink: 0, paddingTop: 1 }}>{statusIcon(effectiveStatus)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.label}</span>
                            {item.required && <span style={{ fontSize: 10, color: '#94a3b8' }}>Required</span>}
                            <span
                              style={statusBadgeStyle(effectiveStatus, isOverridden)}
                              title={canEdit
                                ? isOverridden
                                  ? 'Double-click to revert override'
                                  : autoStatus === 'fail' || autoStatus === 'unavailable'
                                    ? 'Double-click to manually override'
                                    : ''
                                : ''}
                              onDoubleClick={() => handleAutoOverride(item, autoStatus)}
                            >
                              {isOverridden ? 'Override' : effectiveStatus === 'pass' ? 'Pass' : effectiveStatus === 'fail' ? 'Fail' : 'Unavailable'}
                            </span>
                            {result?.link && (
                              <a href={result.link} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: 11, color: '#4B3FFF', textDecoration: 'none' }}>
                                ↗ View
                              </a>
                            )}
                          </div>
                          {isOverridden && (
                            <div style={{ fontSize: 11, color: '#1e40af', marginTop: 2 }}>Manually confirmed by reviewer — double-click to revert</div>
                          )}
                          {result?.detail && !isOverridden && (
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{result.detail}</div>
                          )}
                          {item.autoSource && !result && !isOverridden && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontStyle: 'italic' }}>{item.autoSource}</div>
                          )}
                          {/* Upload button for failed auto-checks that need evidence files */}
                          {canEdit && needsFile && (
                            <div style={{ marginTop: 6 }}>
                              <label style={{ cursor: 'pointer' }}>
                                <input type="file" style={{ display: 'none' }} disabled={isUploading}
                                  onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFileUpload(item.id, item.label, file);
                                    e.target.value = '';
                                  }}
                                />
                                <span style={{
                                  fontSize: 12, padding: '3px 10px', borderRadius: 4,
                                  border: '1px solid #c7d2fe', background: isUploading ? '#e0e7ff' : '#fff',
                                  color: '#4B3FFF', cursor: isUploading ? 'wait' : 'pointer',
                                }}>
                                  {isUploading ? 'Uploading…' : 'Upload Documentation'}
                                </span>
                              </label>
                            </div>
                          )}
                          {/* Show uploaded file if this auto-check has a file linked */}
                          {(() => {
                            const ci = getItemRecord(item.id);
                            return ci?.fileName ? (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                  <span style={{ fontSize: 12, color: '#4B3FFF', fontWeight: 500 }}>{ci.fileName}</span>
                                  {ci.fileDocId && (
                                    <button onClick={() => handleViewDoc(ci.fileDocId!)}
                                      style={{ fontSize: 11, color: '#4B3FFF', background: 'none', border: '1px solid #c7d2fe', borderRadius: 4, padding: '1px 8px', cursor: 'pointer' }}>
                                      View
                                    </button>
                                  )}
                                </div>
                                {canEdit && (
                                  <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                                    <label style={{ cursor: 'pointer' }}>
                                      <input type="file" style={{ display: 'none' }} disabled={isUploading}
                                        onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(item.id, item.label, f); e.target.value = ''; }} />
                                      <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, border: '1px solid #c7d2fe', background: isUploading ? '#e0e7ff' : '#fff', color: '#4B3FFF', cursor: isUploading ? 'wait' : 'pointer' }}>
                                        {isUploading ? 'Uploading…' : 'Replace File'}
                                      </span>
                                    </label>
                                    <button onClick={() => saveItem(item.id, item.label, 'file', 'pending', { fileDocId: '', fileName: '' })}
                                      style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, border: '1px solid #fecaca', background: '#fff', color: '#D33423', cursor: 'pointer' }}>
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* FILE ITEMS */}
              {fileItems.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    File Uploads
                  </div>
                  {fileItems.map(item => {
                    const ci = getItemRecord(item.id);
                    const isUploading = checklistUploading === item.id;
                    const isDone = ci?.status === 'satisfied';
                    const isWaived = ci?.status === 'waived';
                    return (
                      <div key={item.id} style={itemRowStyle}>
                        <div style={{ width: 22, flexShrink: 0, paddingTop: 1 }}>
                          {isDone ? <span style={{ color: '#037730', fontWeight: 700, fontSize: 14 }}>✓</span>
                           : isWaived ? <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 14 }}>~</span>
                           : <span style={{ color: '#cbd5e1', fontSize: 14 }}>○</span>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.label}</span>
                            {item.required && <span style={{ fontSize: 10, color: '#94a3b8' }}>Required</span>}
                            {isDone && <span style={{ ...statusBadgeStyle('pass') }}>Uploaded</span>}
                            {isWaived && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontWeight: 700 }}>Waived</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{item.description}</div>
                          {ci?.fileName && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                              <span style={{ fontSize: 12, color: '#4B3FFF', fontWeight: 500 }}>{ci.fileName}</span>
                              {ci.fileDocId && (
                                <button onClick={() => handleViewDoc(ci.fileDocId!)}
                                  style={{ fontSize: 11, color: '#4B3FFF', background: 'none', border: '1px solid #c7d2fe', borderRadius: 4, padding: '1px 8px', cursor: 'pointer' }}>
                                  View
                                </button>
                              )}
                              {canEdit && isDone && (
                                <button onClick={() => saveItem(item.id, item.label, 'file', 'pending', { fileDocId: '', fileName: '' })}
                                  style={{ fontSize: 11, color: '#D33423', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                  Remove
                                </button>
                              )}
                            </div>
                          )}
                          {isWaived && ci?.waiverReason && (
                            <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>Waiver: {ci.waiverReason}</div>
                          )}
                          {canEdit && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                              <label style={{ cursor: 'pointer' }}>
                                <input type="file" style={{ display: 'none' }} disabled={isUploading}
                                  onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFileUpload(item.id, item.label, file);
                                    e.target.value = '';
                                  }}
                                />
                                <span style={{
                                  fontSize: 12, padding: '3px 10px', borderRadius: 4,
                                  border: '1px solid #c7d2fe', background: isUploading ? '#e0e7ff' : '#fff',
                                  color: '#4B3FFF', cursor: isUploading ? 'wait' : 'pointer',
                                }}>
                                  {isUploading ? 'Uploading…' : isDone ? 'Replace File' : 'Upload'}
                                </span>
                              </label>
                              {item.required && !isWaived && (
                                <button onClick={() => { setChecklistWaiverOpen(item.id); setChecklistWaiverText(''); }}
                                  style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                  Waive
                                </button>
                              )}
                              {isWaived && (
                                <button onClick={() => saveItem(item.id, item.label, 'file', 'pending')}
                                  style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                  Remove Waiver
                                </button>
                              )}
                            </div>
                          )}
                          {/* Waiver inline input */}
                          {checklistWaiverOpen === item.id && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                value={checklistWaiverText}
                                onChange={e => setChecklistWaiverText(e.target.value)}
                                placeholder="Reason for waiver..."
                                style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}
                              />
                              <button onClick={async () => {
                                await saveItem(item.id, item.label, 'file', 'waived', { waiverReason: checklistWaiverText });
                                setChecklistWaiverOpen(null);
                                setChecklistWaiverText('');
                              }} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, border: '1px solid #c7d2fe', background: '#ede9fe', color: '#4B3FFF', cursor: 'pointer' }}>
                                Save
                              </button>
                              <button onClick={() => setChecklistWaiverOpen(null)}
                                style={{ fontSize: 12, padding: '3px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* MANUAL ITEMS */}
              {manualItems.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Manual Confirmation
                  </div>
                  {manualItems.map(item => {
                    const ci = getItemRecord(item.id);
                    const isDone = ci?.status === 'satisfied';
                    const isWaived = ci?.status === 'waived';
                    return (
                      <div key={item.id} style={itemRowStyle}>
                        <div style={{ width: 22, flexShrink: 0, paddingTop: 2 }}>
                          {canEdit ? (
                            <input type="checkbox" checked={isDone || isWaived}
                              onChange={async e => {
                                if (e.target.checked) {
                                  await saveItem(item.id, item.label, 'manual', 'satisfied');
                                } else {
                                  await saveItem(item.id, item.label, 'manual', 'pending');
                                }
                              }}
                              style={{ width: 16, height: 16, accentColor: '#4B3FFF', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: isDone || isWaived ? '#037730' : '#cbd5e1', fontSize: 14 }}>
                              {isDone || isWaived ? '✓' : '○'}
                            </span>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: isDone ? '#037730' : '#1e293b' }}>{item.label}</span>
                            {item.required && <span style={{ fontSize: 10, color: '#94a3b8' }}>Required</span>}
                            {isDone && <span style={{ ...statusBadgeStyle('pass') }}>Confirmed</span>}
                            {isWaived && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontWeight: 700 }}>Waived</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{item.description}</div>
                          {isWaived && ci?.waiverReason && (
                            <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>Waiver: {ci.waiverReason}</div>
                          )}
                          {canEdit && item.required && !isWaived && !isDone && (
                            <button onClick={() => { setChecklistWaiverOpen(item.id); setChecklistWaiverText(''); }}
                              style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                              Waive
                            </button>
                          )}
                          {canEdit && isWaived && (
                            <button onClick={() => saveItem(item.id, item.label, 'manual', 'pending')}
                              style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                              Remove Waiver
                            </button>
                          )}
                          {/* Waiver inline input */}
                          {checklistWaiverOpen === item.id && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input
                                value={checklistWaiverText}
                                onChange={e => setChecklistWaiverText(e.target.value)}
                                placeholder="Reason for waiver..."
                                style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}
                              />
                              <button onClick={async () => {
                                await saveItem(item.id, item.label, 'manual', 'waived', { waiverReason: checklistWaiverText });
                                setChecklistWaiverOpen(null);
                                setChecklistWaiverText('');
                              }} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, border: '1px solid #c7d2fe', background: '#ede9fe', color: '#4B3FFF', cursor: 'pointer' }}>
                                Save
                              </button>
                              <button onClick={() => setChecklistWaiverOpen(null)}
                                style={{ fontSize: 12, padding: '3px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Invoices() {
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';
  const navigate = useNavigate();

  const {
    invoices, loadTickets: allTickets, loadingInvoices,
    loadInvoices, loadLoadTickets, createInvoice, updateInvoice, deleteInvoice,
  } = useDebrisOpsStore();

  const { contractors, trucks, disposalSites, documents, loadContractors, loadTrucks, loadDisposalSites, loadDocuments, createContractor, fetchContractors } = useDebrisStore();
  const { subInvoices, loadSubInvoices, createSubInvoice, updateSubInvoice, deleteSubInvoice } = useTampaDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisInvoice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisInvoice | null>(null);
  const [form, setForm] = useState<Partial<DebrisInvoice>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [matchedTicketIds, setMatchedTicketIds] = useState<number[]>([]);
  const [manualLineItems, setManualLineItems] = useState<ManualLineItem[]>([]);
  const [rateSchedule, setRateSchedule] = useState<RateRow[]>([]);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [qualityChecks, setQualityChecks] = useState<Record<string, boolean>>({
    poReference: false,
    disasterLinkage: false,
    itemizedMaterials: false,
    laborHours: false,
    dailyTickets: false,
    beforeAfterPhotos: false,
  });

  // Invoice detail view state
  const [viewInvoice, setViewInvoice] = useState<DebrisInvoice | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewSortField, setViewSortField] = useState<string>('');
  const [viewSortDir, setViewSortDir] = useState<'asc' | 'desc'>('asc');
  const [viewDocTab, setViewDocTab] = useState<'details' | 'documentation' | 'evidence'>('details');
  const [evidenceDocs, setEvidenceDocs] = useState<Array<{ id: string; title: string; type: string; fileName: string; fileDocId: string; uploadedBy: string; createdAt: string }>>([]);
  const [ticketEvidenceFilter, setTicketEvidenceFilter] = useState<'all' | 'missing' | 'covered'>('all');
  const [ticketClinFilter, setTicketClinFilter] = useState('');
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [viewSubInvoices, setViewSubInvoices] = useState<Array<{id: string; vendor: string; invoiceNumber: string; amount: number; date: string}>>([]);

  // Checklist / Documentation tab state
  const [checklistTemplate, setChecklistTemplate] = useState<ChecklistTemplate | null>(null);
  const [checklistTemplateLoading, setChecklistTemplateLoading] = useState(false);
  const [checklistWaiverOpen, setChecklistWaiverOpen] = useState<string | null>(null); // templateItemId
  const [checklistWaiverText, setChecklistWaiverText] = useState('');
  const [checklistUploading, setChecklistUploading] = useState<string | null>(null); // templateItemId

  // Sync viewInvoice snapshot with store data after reloads (e.g., invoiceType change)
  useEffect(() => {
    if (viewInvoice && invoices.length > 0) {
      const fresh = invoices.find(i => i.id === viewInvoice.id);
      if (fresh && fresh !== viewInvoice) setViewInvoice(fresh);
    }
  }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Checklist items hook — keyed to current viewInvoice
  const checklistInvoiceId = viewInvoice ? String(viewInvoice.id) : null;
  const checklistInvoiceType = (viewInvoice?.invoiceType as InvoiceType | undefined) ?? null;
  const { items: checklistItems, loading: checklistItemsLoading, saveItem: saveChecklistItem } = useChecklistItems(checklistInvoiceId, checklistInvoiceType);

  // AI Invoice Analysis state
  const [aiInvoiceText, setAiInvoiceText] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState<InvoiceAnalysis | null>(null);
  const [aiError, setAiError] = useState('');
  const [aiOpen, setAiOpen] = useState(false);

  // Payment tracking state
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<DebrisInvoice | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'invoices' | 'sub_invoices'>('invoices');

  // Sub-invoice state
  const [subInvModal, setSubInvModal] = useState(false);
  const [editSubInv, setEditSubInv] = useState<any | null>(null);
  const [subInvForm, setSubInvForm] = useState({ primeContractorId: '', subName: '', subInvoiceNumber: '', subInvoiceDate: '', subAmount: '', workType: '', notes: '' });
  const [savingSubInv, setSavingSubInv] = useState(false);

  // Consolidation + supporting docs state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [consolidating, setConsolidating] = useState(false);
  const [allEvidenceDocs, setAllEvidenceDocs] = useState<{id: string; title: string; documentType: string}[]>([]);
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const [docPickerSearch, setDocPickerSearch] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]); // for edit modal

  // Bulk import state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<InvoiceRow[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkUpdateExisting, setBulkUpdateExisting] = useState(false);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const handleBulkFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-selected
    try {
      // Read fresh from stores at call time (not stale closure)
      const freshContractors = useDebrisStore.getState().contractors;
      const freshInvoices = useDebrisOpsStore.getState().invoices;
      const existingContractors = freshContractors
        .filter(c => !incidentId || c.incidentId === incidentId)
        .map(c => ({ id: c.id, companyName: c.companyName || '' }));
      const existingInvoices = freshInvoices
        .filter(i => !incidentId || i.incidentId === incidentId)
        .map(i => ({ id: i.id, invoiceNumber: i.invoiceNumber || '' }));
      const result = await parseInvoiceSpreadsheet(file, existingContractors, existingInvoices);
      setBulkRows(result.rows);
      setBulkErrors(result.unmapped.length > 0
        ? [`Unmapped columns ignored: ${result.unmapped.join(', ')}`]
        : []);
      setBulkOpen(true);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to parse spreadsheet', color: 'var(--color-error)' });
    }
  };

  const handleBulkImport = async () => {
    const actionableRows = bulkRows.filter(r => r.valid && (!r.existingId || bulkUpdateExisting));
    if (!actionableRows.length) return;
    setBulkImporting(true);
    let created = 0;
    let updated = 0;
    let failed = 0;
    // Track auto-created contractors to avoid duplicates within the batch
    const createdContractors = new Map<string, string>(); // normalized name → id
    // Fresh dedup check at import time to catch any invoices created since preview
    const latestInvoices = useDebrisOpsStore.getState().invoices;
    const latestInvNums = new Set(latestInvoices.filter(i => !incidentId || i.incidentId === incidentId).map(i => (i.invoiceNumber || '').toLowerCase().trim()));
    for (const row of actionableRows) {
      try {
        // Auto-create contractor if not matched
        let contractorId = row.matchedContractorId;
        if (!contractorId && row.contractorName) {
          const normName = row.contractorName.toLowerCase().trim();
          // Check if we already created this contractor in this batch
          if (createdContractors.has(normName)) {
            contractorId = createdContractors.get(normName)!;
          } else {
            const newId = await createContractor({ incidentId, companyName: row.contractorName, status: 'active' });
            if (newId) {
              contractorId = String(newId);
              createdContractors.set(normName, contractorId);
            }
          }
        }
        const payload: Partial<DebrisInvoice> = {
          incidentId,
          contractorId,
          invoiceNumber: row.invoiceNumber,
          invoiceDate: row.invoiceDate,
          periodStart: row.periodStart || row.invoiceDate,
          periodEnd: row.periodEnd || row.invoiceDate,
          totalAmount: String(row.totalAmount),
          invoiceType: (row.invoiceType || 'non_debris_contractor') as any,
          status: 'draft',
          notes: row.notes,
        } as any;
        if (row.existingId && bulkUpdateExisting) {
          await updateInvoice(parseInt(row.existingId), payload);
          updated++;
        } else if (!row.existingId) {
          // Skip if this invoice was already created (race condition or re-import)
          const invKey = (row.invoiceNumber || '').toLowerCase().trim();
          if (latestInvNums.has(invKey)) { continue; }
          await createInvoice(payload);
          latestInvNums.add(invKey); // prevent dups within batch
          created++;
        }
      } catch {
        failed++;
      }
    }
    setBulkImporting(false);
    setBulkOpen(false);
    setBulkRows([]);
    setBulkUpdateExisting(false);
    const parts: string[] = [];
    if (created > 0) parts.push(`${created} created`);
    if (updated > 0) parts.push(`${updated} updated`);
    if (failed > 0) parts.push(`${failed} failed`);
    notifications.show({
      message: `Invoices: ${parts.join(', ')}`,
      color: failed > 0 ? 'var(--color-warning)' : 'var(--color-success)',
    });
    loadInvoices();
  };

  useEffect(() => {
    loadInvoices(); loadContractors(); loadLoadTickets(); loadTrucks(); loadDisposalSites(); loadSubInvoices(); loadDocuments();
    // Load rate schedules
    if (TABLES.debrisRateSchedules) {
      const FR = FIELDS.debrisRateSchedules;
      fetchTable(TABLES.debrisRateSchedules).then((rows: any[]) => {
        setRateSchedule(rows.map((r: any) => ({
          lineItemCode: String(r[FR.lineItemCode] ?? ''),
          unit: String(r[FR.unit] ?? ''),
          baseRate: String(r[FR.rateTier1] ?? ''),
          tier2Rate: String(r[FR.rateTier2] ?? ''),
          tier3Rate: String(r[FR.rateTier3] ?? ''),
          tier4Rate: String(r[FR.rateTier4] ?? ''),
          description: String(r[FR.description] ?? ''),
          contractorId: String(r[FR.contractorId] ?? ''),
          incidentId: '',
        })));
      }).catch(() => {});
    }
  }, [loadInvoices, loadContractors, loadLoadTickets, loadTrucks, loadDisposalSites, loadSubInvoices, loadDocuments]);

  // Clear evidence doc cache when incident changes so cross-incident docs don't bleed
  useEffect(() => {
    setAllEvidenceDocs([]);
  }, [incidentId]);

  // Load sub-invoices linked to the open invoice via |||SUBINVOICE||| marker
  useEffect(() => {
    if (!viewInvoice) { setViewSubInvoices([]); return; }
    const FI = FIELDS.debrisInvoices;
    const primeId = String((viewInvoice as any).record_id || viewInvoice.id || '');
    const SUB_RE = /\|\|\|SUBINVOICE\|\|\|([\s\S]*?)\|\|\|ENDSUBINVOICE\|\|\|/;
    const TAMPA_RE = /\|\|\|TAMPA\|\|\|([\s\S]*?)\|\|\|ENDTAMPA\|\|\|/;
    queryRecords<Record<string, unknown>>(TABLES.debrisInvoices, {
      limit: 200, offset: 0,
      filters: [
        { field: FI.incidentId, operator: 'eq' as const, value: incidentId },
        { field: FI.status, operator: 'eq' as const, value: 'sub_invoice' },
      ],
    }).then(res => {
      const matched = (res?.records ?? []).filter(r => {
        const notes = String(r[FI.reviewerNotes] || '');
        const m = notes.match(SUB_RE);
        if (!m) return false;
        try { return String(JSON.parse(m[1]).primeInvoiceId || '') === primeId; } catch { return false; }
      });
      setViewSubInvoices(matched.map(r => {
        const rawNotes = String(r[FI.reviewerNotes] || '');
        let vendor = '';
        const tm = rawNotes.match(TAMPA_RE);
        if (tm) { try { vendor = JSON.parse(tm[1]).vendor || ''; } catch { /* */ } }
        return {
          id: String((r as any).record_id || ''),
          vendor,
          invoiceNumber: String(r[FI.invoiceNumber] || ''),
          amount: parseFloat(String(r[FI.totalAmount] || '0')) || 0,
          date: String(r[FI.periodStart] || '').slice(0, 10),
        };
      }));
    }).catch(() => setViewSubInvoices([]));
  }, [viewInvoice, incidentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inline evidence preview state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewType, setPreviewType] = useState<'pdf' | 'image' | 'other'>('other');

  const handleViewEvidenceDoc = async (documentId: string, title?: string) => {
    setPreviewLoading(true);
    setPreviewTitle(title || 'Document');
    try {
      const url = await getV2FileDownloadUrl(documentId);
      const lower = (title || '').toLowerCase();
      if (lower.endsWith('.pdf') || lower.includes('.pdf')) {
        setPreviewType('pdf');
      } else if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(lower)) {
        setPreviewType('image');
      } else {
        // Try to detect from URL or default to iframe (works for PDFs from S3)
        setPreviewType(url.includes('.pdf') || url.includes('application/pdf') ? 'pdf' : 'pdf');
      }
      setPreviewUrl(url);
    } catch {
      notifications.show({ message: 'Could not retrieve document URL', color: 'var(--color-error)' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => { setPreviewUrl(null); setPreviewTitle(''); };

  // Load evidence documents when evidence tab is opened
  const loadEvidenceDocs = useCallback(async () => {
    if (!viewInvoice) return;
    setEvidenceLoading(true);
    const invId = String(viewInvoice.id);
    const docs: typeof evidenceDocs = [];
    try {
      // 1. Checklist items with uploaded files
      for (const ci of checklistItems) {
        if (ci.fileDocId && ci.status === 'satisfied') {
          docs.push({
            id: `cl-${ci.id}`, title: ci.label, type: 'checklist',
            fileName: ci.fileName || 'Uploaded file', fileDocId: ci.fileDocId,
            uploadedBy: ci.checkedBy || '', createdAt: ci.checkedAt || '',
          });
        }
      }
      // 2. Documents linked via INVOICELINK marker in debrisDocuments notes
      const FDoc = FIELDS.debrisDocuments;
      const docRes = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
        limit: 100,
        ...(incidentId ? { filters: [{ field: FDoc.incidentId, operator: 'eq' as const, value: incidentId }] } : {}),
      });
      for (const r of docRes.records) {
        const notes = String(r[FDoc.notes] || '');
        const linked = notes.includes(`"invoiceId":"${invId}"`) || notes.includes(`"invoiceId": "${invId}"`);
        if (linked) {
          const fileField = r[FDoc.file];
          const fileDocId = fileField ? String(typeof fileField === 'object' ? (fileField as any).id || '' : fileField) : '';
          docs.push({
            id: String(r.record_id), title: String(r[FDoc.title] || 'Document'),
            type: String(r[FDoc.documentType] || 'other'),
            fileName: String(r[FDoc.title] || ''),
            fileDocId,
            uploadedBy: String(r[FDoc.uploadedBy] || ''),
            createdAt: String((r as any).created_at || ''),
          });
        }
      }
    } catch (e) { console.warn('[Invoices] loadEvidenceDocs', e); }
    setEvidenceDocs(docs);
    setEvidenceLoading(false);
  }, [viewInvoice, checklistItems, incidentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // CY calculation with fallback to load% × truck capacity
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = trucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [trucks]);

  const iInvoices = incidentId ? invoices : [];
  const iContractors = incidentId ? contractors : [];
  const { tickets: allPagedTickets, loading: ticketsLoading } = useAllTickets();
  const iTickets = allPagedTickets;

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);

  // Define before filtered so the search filter can use it with correct deps
  const contractorName = useCallback(
    (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? (id || '-'),
    [contractors],
  );

  // Set of contractor IDs that were auto-created by PackageProcessor (need manual verification)
  const AUTO_CONT_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
  const autoCreatedContractorIds = useMemo(() => {
    const ids = new Set<string>();
    contractors.forEach(c => {
      const m = (c.notes ?? '').match(AUTO_CONT_RE);
      if (m) { try { const meta = JSON.parse(m[1]); if (meta.autoCreated === 'true') ids.add(String(c.id)); } catch { /* */ } }
    });
    return ids;
  }, [contractors]);

  const filtered = useMemo(() => {
    let list = iInvoices;
    if (statusFilter && statusFilter !== 'all') list = list.filter(i => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.invoiceNumber.toLowerCase().includes(q) ||
        contractorName(i.contractorId).toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [iInvoices, statusFilter, search, contractorName]);
  const setField = (key: keyof DebrisInvoice, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  // ── Auto-calculate from tickets using rate schedule + distance tiers ──
  const calculate = useCallback(() => {
    const cId = form.contractorId;
    const start = form.periodStart;
    const end = form.periodEnd;

    if (!cId) {
      notifications.show({ message: 'Select a contractor first', color: '#C68700' });
      return;
    }
    if (ticketsLoading) {
      notifications.show({ message: 'Tickets still loading — try again in a moment', color: '#C68700' });
      return;
    }
    if (iTickets.length === 0) {
      notifications.show({ message: 'No tickets loaded for this incident. Try refreshing the page.', color: '#C68700' });
      return;
    }

    // Get rate schedule for this contractor + incident
    const cRates = rateSchedule.filter(r => r.contractorId === cId && (!r.incidentId || r.incidentId === incidentId));
    const hasRateSchedule = cRates.length > 0;

    // Fallback to old flat rates if no rate schedule
    const contractor = contractors.find(c => String(c.id) === cId);
    if (!contractor) return;

    // Filter tickets: matching contractor, within date range, not voided
    const matched = iTickets.filter(t => {
      // Include tickets assigned to this contractor OR unassigned (empty contractorId)
      if (t.contractorId && t.contractorId !== cId) return false;
      if (t.status === 'voided') return false;
      const tDate = toISODate(t.loadDate);
      if (start && tDate && tDate < start) return false;
      if (end && tDate && tDate > end) return false;
      return true;
    });

    // ── Path A: tickets have CLIN billing data from XLSX enrichment ──────────
    // Group by clinCode — use actual extendedCost/transactionRate stored on tickets.
    // This is the most accurate path and doesn't require a rate schedule table.
    const ticketsWithBilling = matched.filter(t => t.clinCode && parseFloat(t.extendedCost || '0') > 0);
    const useBillingData = !hasRateSchedule && ticketsWithBilling.length > 0;

    let items: LineItem[];

    if (useBillingData) {
      // Group by CLIN code, sum CY and cost from ticket fields
      const clinGroups: Record<string, { qty: number; cost: number; rate: number; count: number; desc: string }> = {};
      ticketsWithBilling.forEach(t => {
        const clin = t.clinCode!;
        const cy = getTicketCY(t);
        const cost = parseFloat(t.extendedCost || '0');
        const rate = parseFloat(t.transactionRate || '0');
        const desc = t.clinDescription || clin;
        if (!clinGroups[clin]) clinGroups[clin] = { qty: 0, cost: 0, rate: 0, count: 0, desc };
        clinGroups[clin].qty += cy;
        clinGroups[clin].cost += cost;
        if (rate > 0) clinGroups[clin].rate = rate;
        clinGroups[clin].count++;
      });

      items = Object.entries(clinGroups)
        .map(([clin, g]) => ({
          debrisType: 'vegetative',
          label: g.desc || clin,
          quantity: Math.round(g.qty * 10) / 10,
          unit: 'CY',
          rate: Math.round(g.rate * 100) / 100,
          amount: Math.round(g.cost * 100) / 100,
          ticketCount: g.count,
          tier: '',
          lineItemCode: clin,
        }))
        .sort((a, b) => b.amount - a.amount);

    } else {
      // ── Path B: rate schedule or debris-type grouping ─────────────────────
      const TIER_NAMES = ['Tier 1 (0–15mi)', 'Tier 2 (16–30mi)', 'Tier 3 (31–60mi)', 'Tier 4 (60+mi)'];
      const groupKey = (type: string, tier: number) => `${type}__${tier}`;
      const groups: Record<string, { qty: number; count: number; tier: number; type: string }> = {};

      matched.forEach(t => {
        const dt = t.debrisType || 'unknown';
        const cfg = DEBRIS_TYPES[dt as DebrisType];
        const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
        const site = disposalSites.find(s => String(s.id) === t.disposalSiteId);
        const pLat = parseFloat(t.pickupLat); const pLng = parseFloat(t.pickupLng);
        const sLat = site ? parseFloat((site as any).latitude) : 0;
        const sLng = site ? parseFloat((site as any).longitude) : 0;
        const tier = getDistanceTier(pLat, pLng, sLat, sLng);
        let qty = 0;
        if (mode === 'volumetric') qty = getTicketCY(t);
        else if (mode === 'unit') qty = parseFloat(t.unitCount || '0');
        else if (mode === 'weight') qty = parseFloat(t.weight || '0');
        const key = groupKey(dt, tier);
        if (!groups[key]) groups[key] = { qty: 0, count: 0, tier, type: dt };
        groups[key].qty += qty;
        groups[key].count++;
      });

      items = Object.values(groups)
        .filter(g => g.qty > 0)
        .map(g => {
          const cfg = DEBRIS_TYPES[g.type as DebrisType];
          const mode = cfg?.measurementMode || 'volumetric';
          const unit = mode === 'volumetric' ? 'CY' : mode === 'unit' ? (cfg?.unitLabel || 'units') : (cfg?.weightUnit || 'lbs');
          const lineCode = TYPE_TO_LINE_ITEM[g.type] || '1';
          let rate = 0;
          let usedCode = lineCode;
          if (hasRateSchedule) {
            const rateRow = cRates.find(r => r.lineItemCode === lineCode);
            if (rateRow) {
              const scheduleUnit = rateRow.unit?.toUpperCase() || '';
              const ticketIsUnitCount = mode === 'unit';
              const scheduleIsWeight = scheduleUnit === 'LB' || scheduleUnit === 'LBS';
              if (ticketIsUnitCount && scheduleIsWeight && UNIT_RATE_OVERRIDE[g.type]) {
                rate = UNIT_RATE_OVERRIDE[g.type];
              } else {
                rate = getRateForTier(rateRow, g.tier);
              }
              usedCode = rateRow.lineItemCode;
            } else if (UNIT_RATE_OVERRIDE[g.type] && mode === 'unit') {
              rate = UNIT_RATE_OVERRIDE[g.type];
            }
          }
          return {
            debrisType: g.type,
            label: DEBRIS_TYPE_LABELS[g.type as DebrisType] ?? g.type,
            quantity: Math.round(g.qty * 10) / 10,
            unit,
            rate,
            amount: Math.round(g.qty * rate * 100) / 100,
            ticketCount: g.count,
            tier: TIER_NAMES[g.tier],
            lineItemCode: usedCode,
          };
        })
        .sort((a, b) => b.amount - a.amount);
    }

    const totalQty = items.reduce((s, i) => s + i.quantity, 0);
    const totalAmt = items.reduce((s, i) => s + i.amount, 0);

    setLineItems(items);
    setMatchedTicketIds(matched.map(t => t.id));
    // Do NOT overwrite totalAmount — that is what the City paid (the anchor).
    // calculate() only populates substantiation line items.
    setForm(prev => ({
      ...prev,
      totalCubicYards: totalQty.toFixed(1),
      ticketIds: JSON.stringify(matched.map(t => t.id)),
      lineItems: JSON.stringify(items),
    }));

    const modeLabel = useBillingData ? ' (from TDMS billing data)' : hasRateSchedule ? ' (rate schedule applied)' : ' (no rate data)';
    notifications.show({
      message: `${matched.length}/${iTickets.length} tickets matched → ${items.length} line items${modeLabel} → $${totalAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      color: useBillingData || hasRateSchedule ? 'var(--color-success)' : '#C68700',
    });
  }, [form.contractorId, form.periodStart, form.periodEnd, iTickets, ticketsLoading, contractors, rateSchedule, disposalSites, incidentId]);

  // Auto-generate next invoice number
  const nextInvoiceNumber = useMemo(() => {
    const year = new Date().getFullYear();
    const prefix = `INV-${year}-`;
    const existing = iInvoices
      .map(i => i.invoiceNumber)
      .filter(n => n.startsWith(prefix))
      .map(n => parseInt(n.replace(prefix, ''), 10))
      .filter(n => !isNaN(n));
    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${prefix}${String(next).padStart(3, '0')}`;
  }, [iInvoices]);

  // ── Invoice Quality Gate ─────────────────────────────────
  const QUALITY_ITEMS = [
    { key: 'poReference', label: 'PO/WO Reference present', critical: true },
    { key: 'disasterLinkage', label: 'Disaster event linkage', critical: true },
    { key: 'itemizedMaterials', label: 'Itemized materials with supplier invoice numbers', critical: false },
    { key: 'laborHours', label: 'Labor hours by classification with rates', critical: false },
    { key: 'dailyTickets', label: 'Daily service tickets attached', critical: false },
    { key: 'beforeAfterPhotos', label: 'Before/after photos (for property work)', critical: false },
  ];

  const computeQualityScore = useCallback((checks: Record<string, boolean>): InvoiceQualityScore => {
    const checklist = QUALITY_ITEMS.map(item => ({
      item: item.label,
      present: !!checks[item.key],
    }));
    const missingItems = QUALITY_ITEMS.filter(item => !checks[item.key]).map(item => item.label);
    const criticalMissing = QUALITY_ITEMS.filter(item => item.critical && !checks[item.key]);
    const nonCriticalMissing = QUALITY_ITEMS.filter(item => !item.critical && !checks[item.key]);

    let score: InvoiceQualityScore['score'] = 'complete';
    if (criticalMissing.length > 0) score = 'reject';
    else if (nonCriticalMissing.length > 0) score = 'needs_revision';

    return { score, missingItems, checklist };
  }, []);

  const generateDeficiencyLetter = useCallback((inv: Partial<DebrisInvoice>, checks: Record<string, boolean>) => {
    const missing = QUALITY_ITEMS.filter(item => !checks[item.key]).map(item => item.label);
    if (missing.length === 0) return '';
    const cName = contractorName(inv.contractorId ?? '');
    return `Invoice #${inv.invoiceNumber || '?'} from ${cName} is missing: ${missing.join('; ')}. Please resubmit with these items per City of Tampa invoice requirements.`;
  }, [contractorName]);

  // ── AI Invoice Analysis handler ──────────────────────────
  const handleAiAnalyze = async () => {
    if (!aiInvoiceText.trim()) {
      notifications.show({ message: 'Paste invoice text to analyze', color: '#C68700' });
      return;
    }
    setAiAnalyzing(true);
    setAiError('');
    setAiResult(null);
    try {
      const result = await analyzeInvoice(aiInvoiceText);
      setAiResult(result);

      // Auto-fill form fields from AI analysis — fuzzy contractor name match
      if (result.vendor && !form.contractorId) {
        const normVendor = result.vendor.toLowerCase()
          .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, ' ')
          .replace(/\b(inc|llc|corp|ltd|co|company|environmental|services|construction|contractors?|group|enterprises?|solutions|management|systems|industries|national|associates?|partners?)\b/g, '')
          .replace(/\s+/g, ' ').trim();
        const match = contractors.find(c => {
          const normStored = (c.companyName ?? '').toLowerCase()
            .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, ' ')
            .replace(/\b(inc|llc|corp|ltd|co|company|environmental|services|construction|contractors?|group|enterprises?|solutions|management|systems|industries|national|associates?|partners?)\b/g, '')
            .replace(/\s+/g, ' ').trim();
          return normStored && normVendor && (normStored === normVendor || normStored.includes(normVendor) || normVendor.includes(normStored));
        }) ?? contractors.find(c => c.companyName?.toLowerCase().includes(result.vendor.toLowerCase()));
        if (match) setField('contractorId', String(match.id));
      }
      if (result.invoiceNumber && !form.invoiceNumber) setField('invoiceNumber', result.invoiceNumber);
      if (result.totalAmount > 0 && !form.totalAmount) setField('totalAmount', String(result.totalAmount));
      if (result.invoiceDate) {
        if (!form.periodStart) setField('periodStart', result.invoiceDate);
      }

      // Auto-fill quality checks from AI
      setQualityChecks(prev => ({
        ...prev,
        poReference: !!result.poNumber || prev.poReference,
        disasterLinkage: !!result.disasterReference || prev.disasterLinkage,
        itemizedMaterials: result.materialsItemized || prev.itemizedMaterials,
        laborHours: result.laborBreakdown.length > 0 || prev.laborHours,
        dailyTickets: result.dailyServiceTickets || prev.dailyTickets,
      }));

      notifications.show({
        message: `AI analysis complete: ${result.qualityScore === 'complete' ? 'All checks passed' : result.missingItems.length + ' items missing'}`,
        color: result.qualityScore === 'complete' ? 'var(--color-success)' : result.qualityScore === 'needs_revision' ? '#C68700' : 'var(--color-error)',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'AI analysis unavailable';
      setAiError(msg);
      notifications.show({ message: `AI analysis failed: ${msg}`, color: 'var(--color-error)' });
    } finally {
      setAiAnalyzing(false);
    }
  };

  // ── Payment tracking helpers ────────────────────────────
  const PAYMENTS_RE = /\|\|\|PAYMENTS\|\|\|([\s\S]*?)\|\|\|ENDPAYMENTS\|\|\|/;

  const getPayments = useCallback((inv: DebrisInvoice): { date: string; amount: number; reference: string; notes: string }[] => {
    const rawNotes = inv.notes ?? '';
    const m = rawNotes.match(PAYMENTS_RE);
    if (!m) return [];
    try { return JSON.parse(m[1]); } catch { return []; }
  }, []);

  const addPayment = async (inv: DebrisInvoice) => {
    const rawAmount = paymentAmount.trim();
    const parsedAmount = parseFloat(rawAmount.replace(/[^0-9.]/g, ''));
    if (!paymentDate || !rawAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
      notifications.show({ message: 'Date and a positive amount are required', color: '#C68700' });
      return;
    }
    const existing = getPayments(inv);
    const newPayment = {
      date: paymentDate,
      amount: parsedAmount,
      reference: paymentRef,
      notes: paymentNotes,
    };
    const updated = [...existing, newPayment];
    const cleanNotes = (inv.notes ?? '').replace(PAYMENTS_RE, '').trim();
    const newNotes = cleanNotes
      ? `${cleanNotes}\n|||PAYMENTS|||${JSON.stringify(updated)}|||ENDPAYMENTS|||`
      : `|||PAYMENTS|||${JSON.stringify(updated)}|||ENDPAYMENTS|||`;

    try {
      await updateInvoice(inv.id, { notes: newNotes });
      notifications.show({ message: `Payment of $${parsedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} recorded`, color: 'var(--color-success)' });
      setPaymentDate('');
      setPaymentAmount('');
      setPaymentRef('');
      setPaymentNotes('');
      // Update selected invoice directly so the modal reflects the new payment immediately,
      // without depending on loadInvoices() round-trip timing.
      setSelectedInvoice(prev => prev ? { ...prev, notes: newNotes } : null);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to record payment', color: 'var(--color-error)' });
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, invoiceNumber: nextInvoiceNumber });
    setLineItems([]);
    setMatchedTicketIds([]);
    setManualLineItems([]);
    setQualityOpen(false);
    setQualityChecks({ poReference: false, disasterLinkage: false, itemizedMaterials: false, laborHours: false, dailyTickets: false, beforeAfterPhotos: false });
    setAiInvoiceText('');
    setAiResult(null);
    setAiError('');
    setAiOpen(false);
    setSelectedDocIds([]);
    setModalOpen(true);
  };

  const openEdit = (inv: DebrisInvoice) => {
    setEditTarget(inv);
    // Decode Tampa meta to populate form fields
    const tampaMeta = decodeTampaMeta(inv.notes ?? '');
    const cleanNotes = (inv.notes ?? '').replace(TAMPA_META_RE, '').replace(INVOICE_META_RE, '').trim();
    setForm({
      ...inv,
      notes: cleanNotes,
      _tampaContractorSubmitted: String(tampaMeta.contractorSubmittedAmount ?? ''),
      _tampaMonitorVerified: String(tampaMeta.monitorVerifiedAmount ?? ''),
      _tampaPaymentRef: tampaMeta.paymentRef ?? '',
    } as any);
    // Restore line items from stored JSON — split ticket-based vs manual entries
    try {
      const allItems: any[] = JSON.parse(inv.lineItems || '[]');
      // Ticket-based items have `debrisType`; manual items have `description` (no debrisType)
      const ticketItems = allItems.filter(i => i.debrisType);
      const manualItems = allItems.filter(i => !i.debrisType && (i.description !== undefined));
      setLineItems(ticketItems as LineItem[]);
      setManualLineItems(manualItems.map((i, idx) => ({
        id: String(i.id || `m-${idx}`),
        description: String(i.description || ''),
        quantity: String(i.quantity ?? ''),
        unit: String(i.unit || ''),
        rate: String(i.rate ?? ''),
        amount: String(i.amount ?? ''),
      })));
    } catch { setLineItems([]); setManualLineItems([]); }
    try { setMatchedTicketIds(JSON.parse(inv.ticketIds || '[]')); } catch { setMatchedTicketIds([]); }
    // Restore quality checks from TAMPA meta (use full extraction which carries qualityChecks)
    const fullMeta = decodeFullTampaMeta(inv.notes ?? '');
    const qc = fullMeta.qualityChecks ?? tampaMeta.qualityChecks ?? {};
    setQualityChecks({
      poReference: !!qc.poReference,
      disasterLinkage: !!qc.disasterLinkage,
      itemizedMaterials: !!qc.itemizedMaterials,
      laborHours: !!qc.laborHours,
      dailyTickets: !!qc.dailyTickets,
      beforeAfterPhotos: !!qc.beforeAfterPhotos,
    });
    setSelectedDocIds((tampaMeta.supportingDocIds as string[]) || []);
    setQualityOpen(false);
    setModalOpen(true);
  };

  const openView = (inv: DebrisInvoice) => {
    setViewInvoice(inv);
    setViewSortField('');
    setViewSortDir('asc');
    setViewDocTab('details');
    setChecklistTemplate(null);
    setChecklistWaiverOpen(null);
    setChecklistWaiverText('');
    setViewOpen(true);
  };

  // Load checklist template when invoiceType is set
  const loadChecklistTemplate = async (invType: InvoiceType) => {
    setChecklistTemplateLoading(true);
    try {
      const res = await apiFetchRecords<Record<string, unknown>>(TABLES.debrisChecklistTemplates, { limit: 20 });
      const FCT = FIELDS.debrisChecklistTemplates;
      const record = res.records.find(r => String(r[FCT.invoiceType] ?? '') === invType);
      if (record) {
        const raw = String(record[FCT.config] ?? '');
        try {
          const parsed = JSON.parse(raw);
          setChecklistTemplate({ invoiceType: invType, items: parsed.items ?? [] });
        } catch {
          setChecklistTemplate(DEFAULT_TEMPLATES.find(t => t.invoiceType === invType) ?? null);
        }
      } else {
        setChecklistTemplate(DEFAULT_TEMPLATES.find(t => t.invoiceType === invType) ?? null);
      }
    } catch {
      setChecklistTemplate(DEFAULT_TEMPLATES.find(t => t.invoiceType === invType) ?? null);
    }
    setChecklistTemplateLoading(false);
  };

  // Auto-calculate when contractor + period are set
  useEffect(() => {
    if (modalOpen && form.contractorId && form.periodStart && form.periodEnd && !editTarget) {
      calculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contractorId, form.periodStart, form.periodEnd, modalOpen]);

  const handleSave = async () => {
    if (!form.invoiceNumber?.trim()) {
      notifications.show({ message: 'Invoice number is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      // Build payload with Tampa meta injected into notes for IMETA encoding
      // The store's createInvoice/updateInvoice will merge these into the IMETA block
      const formAny = form as any;
      const tampaMetaFields: Record<string, unknown> = {};
      if (formAny._tampaContractorSubmitted) tampaMetaFields.contractorSubmittedAmount = parseFloat(formAny._tampaContractorSubmitted) || 0;
      if (formAny._tampaMonitorVerified) tampaMetaFields.monitorVerifiedAmount = parseFloat(formAny._tampaMonitorVerified) || 0;
      if (formAny._tampaPaymentRef) tampaMetaFields.paymentRef = formAny._tampaPaymentRef;
      // Persist quality checks
      tampaMetaFields.qualityChecks = qualityChecks;
      if (selectedDocIds.length > 0) tampaMetaFields.supportingDocIds = selectedDocIds;

      // Strip _tampa* keys from payload and clean notes of existing IMETA before the store re-encodes
      const cleanForm = { ...form };
      Object.keys(cleanForm).forEach(k => { if (k.startsWith('_tampa')) delete (cleanForm as any)[k]; });

      // Merge ticket-based line items + manual line items into lineItems JSON
      const serializedManual = manualLineItems
        .filter(i => i.description.trim())
        .map(i => ({
          description: i.description,
          quantity: parseFloat(i.quantity) || 0,
          unit: i.unit,
          rate: parseFloat(i.rate) || 0,
          amount: parseFloat(i.amount) || (parseFloat(i.quantity) * parseFloat(i.rate)) || 0,
        }));
      cleanForm.lineItems = JSON.stringify([...lineItems, ...serializedManual]);

      // Recalculate totalAmount from all line items if any are present
      const allItems = [...lineItems, ...serializedManual];
      if (allItems.length > 0 && !form.totalAmount) {
        const total = allItems.reduce((s, i) => s + (i.amount || 0), 0);
        cleanForm.totalAmount = total.toFixed(2);
      }

      const payload = {
        ...cleanForm,
        incidentId,
      } as any;

      // Inject Tampa meta into the notes text as a separate marker
      let userNotes = (cleanForm.notes ?? '').replace(/\|\|\|TAMPA\|\|\|[\s\S]*?\|\|\|ENDTAMPA\|\|\|/g, '').trim();
      if (Object.keys(tampaMetaFields).length > 0) {
        userNotes = userNotes
          ? `${userNotes}\n|||TAMPA|||${JSON.stringify(tampaMetaFields)}|||ENDTAMPA|||`
          : `|||TAMPA|||${JSON.stringify(tampaMetaFields)}|||ENDTAMPA|||`;
      }
      payload.notes = userNotes;

      if (editTarget) {
        await updateInvoice(editTarget.id, payload);
        notifications.show({ message: 'Invoice updated', color: 'var(--color-success)' });
      } else {
        const created = await createInvoice(payload);
        void logActivity('invoice', 'invoice.created', 'invoice', (created as any)?.id ?? 'new', {
          invoiceNumber: payload.invoiceNumber, contractorId: payload.contractorId,
        });
        notifications.show({ message: 'Invoice created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteInvoice(deleteTarget.id);
      void logActivity('invoice', 'invoice.deleted', 'invoice', deleteTarget.id, {
        invoiceNumber: deleteTarget.invoiceNumber,
      });
      notifications.show({ message: `Invoice ${deleteTarget.invoiceNumber} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleApprove = async (inv: DebrisInvoice) => {
    try {
      await updateInvoice(inv.id, { status: 'approved', approvedBy: user?.email ?? '' });
      void logActivity('invoice', 'invoice.approved', 'invoice', inv.id, {
        invoiceNumber: inv.invoiceNumber, totalAmount: inv.totalAmount,
      });
      notifications.show({ message: `Invoice ${inv.invoiceNumber} approved`, color: 'var(--color-success)' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Approve failed', color: 'var(--color-error)' });
    }
  };

  const handleConsolidate = async () => {
    const selected = iInvoices.filter(i => selectedIds.has(i.id));
    if (selected.length < 2) return;

    // Validate: all same contractor
    const contractorIds = new Set(selected.map(i => i.contractorId));
    if (contractorIds.size > 1) {
      notifications.show({ message: 'All selected invoices must be from the same contractor', color: '#C68700' });
      return;
    }

    // Validate: none already consolidated or paid
    const invalid = selected.filter(i => i.status === 'consolidated' || i.status === 'paid');
    if (invalid.length > 0) {
      notifications.show({ message: `Cannot consolidate invoices that are already ${invalid[0].status}`, color: 'var(--color-error)' });
      return;
    }

    setConsolidating(true);
    try {
      // Merge all line items
      const mergedLineItems: any[] = [];
      let totalAmt = 0;
      let periodStart = '';
      let periodEnd = '';

      for (const inv of selected) {
        try {
          const items = JSON.parse(inv.lineItems || '[]');
          mergedLineItems.push(...items);
        } catch { /* */ }
        totalAmt += parseFloat(inv.totalAmount || '0');
        if (!periodStart || inv.periodStart < periodStart) periodStart = inv.periodStart;
        if (!periodEnd || inv.periodEnd > periodEnd) periodEnd = inv.periodEnd;
      }

      const cId = selected[0].contractorId;
      const year = new Date().getFullYear();
      const prefix = `CONS-${year}-`;
      const existing = iInvoices.filter(i => i.invoiceNumber?.startsWith(prefix));
      const nextNum = existing.length + 1;
      const newInvoiceNumber = `${prefix}${String(nextNum).padStart(3, '0')}`;

      const tampaBlock = `|||TAMPA|||${JSON.stringify({
        sourceInvoices: selected.map(i => i.id),
      })}|||ENDTAMPA|||`;

      // Create consolidated parent invoice
      const created = await createInvoice({
        contractorId: cId,
        invoiceNumber: newInvoiceNumber,
        periodStart,
        periodEnd,
        totalAmount: totalAmt.toFixed(2),
        lineItems: JSON.stringify(mergedLineItems),
        status: 'draft',
        notes: tampaBlock,
        incidentId,
      } as any);

      if (!created) throw new Error('Failed to create consolidated invoice');

      // Mark source invoices as consolidated
      await Promise.all(selected.map(async inv => {
        const rawNotes = inv.notes ?? '';
        const cleanNotes = rawNotes.replace(/\|\|\|TAMPA\|\|\|[\s\S]*?\|\|\|ENDTAMPA\|\|\|/g, '').trim();
        const existingTampa = decodeTampaMeta(rawNotes);
        const newTampa = { ...existingTampa, consolidatedInto: (created as any).id };
        const newNotes = cleanNotes
          ? `${cleanNotes}\n|||TAMPA|||${JSON.stringify(newTampa)}|||ENDTAMPA|||`
          : `|||TAMPA|||${JSON.stringify(newTampa)}|||ENDTAMPA|||`;
        await updateInvoice(inv.id, { status: 'consolidated', notes: newNotes });
      }));

      setSelectedIds(new Set());
      void logActivity('invoice', 'invoice.consolidated', 'invoice', (created as any)?.id ?? 'new', {
        invoiceNumber: newInvoiceNumber, sourceCount: selected.length, totalAmount: totalAmt.toFixed(2),
      });
      notifications.show({
        message: `${selected.length} invoices consolidated into ${newInvoiceNumber} — total ${fmtMoney(totalAmt)}`,
        color: 'var(--color-success)',
      });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Consolidation failed', color: 'var(--color-error)' });
    } finally {
      setConsolidating(false);
    }
  };

  // Sanitize text for jsPDF (only supports basic Latin chars)
  const pdfSafe = (s: string) => s.replace(/[^\x20-\x7E]/g, c => {
    if (c === '\u2014' || c === '\u2013') return '-'; // em/en dash
    if (c === '\u2018' || c === '\u2019') return "'";
    if (c === '\u201C' || c === '\u201D') return '"';
    return '';
  });

  // Build line items from live ticket data for a given invoice
  const buildLineItemsFromTickets = (inv: DebrisInvoice): LineItem[] => {
    const contractor = contractors.find(c => String(c.id) === inv.contractorId);
    if (!contractor) return [];

    // Use stored ticket IDs first (exact match from when invoice was calculated)
    let storedIds: number[] = [];
    try { storedIds = JSON.parse(inv.ticketIds || '[]'); } catch { /* */ }

    let matched = storedIds.length > 0
      ? iTickets.filter(t => storedIds.includes(t.id))
      : iTickets.filter(t => {
          if (t.contractorId !== inv.contractorId) return false;
          if (t.status === 'voided') return false;
          const tDate = toISODate(t.loadDate);
          if (inv.periodStart && tDate && tDate < inv.periodStart) return false;
          if (inv.periodEnd && tDate && tDate > inv.periodEnd) return false;
          return true;
        });

    const byType: Record<string, { qty: number; count: number; unit: string }> = {};
    matched.forEach(t => {
      const dt = t.debrisType || 'unknown';
      const cfg = DEBRIS_TYPES[dt as DebrisType];
      const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
      if (!byType[dt]) byType[dt] = { qty: 0, count: 0, unit: mode === 'volumetric' ? 'CY' : mode === 'unit' ? (cfg?.unitLabel || 'units') : 'lbs' };
      if (mode === 'volumetric') byType[dt].qty += getTicketCY(t);
      else if (mode === 'unit') byType[dt].qty += parseFloat(t.unitCount || '0');
      else if (mode === 'weight') byType[dt].qty += parseFloat(t.weight || '0');
      byType[dt].count++;
    });

    return Object.entries(byType)
      .filter(([, v]) => v.qty > 0)
      .map(([type, v]) => ({
        debrisType: type,
        label: DEBRIS_TYPE_LABELS[type as DebrisType] ?? type,
        quantity: Math.round(v.qty * 10) / 10,
        unit: v.unit,
        rate: 0,
        amount: 0,
        ticketCount: v.count,
        tier: '',
        lineItemCode: TYPE_TO_LINE_ITEM[type] || '1',
      }))
      .sort((a, b) => b.quantity - a.quantity);
  };

  const generatePDF = (inv: DebrisInvoice) => {
    try {
      const doc = new jsPDF();
      const cName = pdfSafe(contractorName(inv.contractorId));
      const totalAmt = parseFloat(inv.totalAmount || '0');
      const fmtMon = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const fmtN = (n: number) => n.toLocaleString();

      // Parse AI extraction data
      const tampaMeta = decodeFullTampaMeta(inv.notes || '');
      const hasAI = !!(tampaMeta.vendor || tampaMeta.jobDescription);

      // Parse line items
      let items: any[] = [];
      try { items = JSON.parse(inv.lineItems || '[]'); } catch {}

      // Detect invoice type: debris hauler (has CLIN codes) vs vendor (has descriptions)
      const isDebrisHauler = items.some((i: any) => i.clinCode || i.debrisType);
      const isVendorInvoice = !isDebrisHauler && (hasAI || items.some((i: any) => i.description));

      // ── Header ──
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(isVendorInvoice ? 'CONTRACTOR INVOICE' : 'DEBRIS REMOVAL INVOICE', 14, 22);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('OpenGov Emergency Management Console — City of Tampa', 14, 29);
      doc.setTextColor(0);

      // ── Invoice Info ──
      let y = 40;
      const addField = (label: string, value: string) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 14, y);
        doc.setFont('helvetica', 'normal');
        doc.text(pdfSafe(value), 55, y);
        y += 7;
      };

      addField('Invoice #:', inv.invoiceNumber || '-');
      addField('Contractor:', tampaMeta.vendor || cName || '-');
      if (tampaMeta.vendorAddress) addField('Address:', tampaMeta.vendorAddress);
      addField('Date:', fmtDate(inv.periodStart) === fmtDate(inv.periodEnd)
        ? fmtDate(inv.periodStart)
        : `${fmtDate(inv.periodStart)} to ${fmtDate(inv.periodEnd)}`);
      if (tampaMeta.poNumber) addField('PO / WO #:', tampaMeta.poNumber);
      addField('Status:', (inv.status || 'draft').toUpperCase());
      if (tampaMeta.disasterReference) addField('Disaster:', tampaMeta.disasterReference);
      else if (activeIncident?.name) addField('Incident:', activeIncident.name);

      // ── Total Amount ──
      y += 3;
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total: ${fmtMon(totalAmt)}`, 14, y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      y += 8;

      // ── Job Description (vendor invoices) ──
      if (tampaMeta.jobDescription) {
        doc.setFont('helvetica', 'bold');
        doc.text('Work Description:', 14, y);
        doc.setFont('helvetica', 'normal');
        y += 5;
        const descLines = doc.splitTextToSize(pdfSafe(tampaMeta.jobDescription), 180);
        doc.text(descLines, 14, y);
        y += descLines.length * 5 + 3;
      }
      if (tampaMeta.jobLocation) {
        doc.setFont('helvetica', 'bold');
        doc.text('Location:', 14, y);
        doc.setFont('helvetica', 'normal');
        doc.text(pdfSafe(tampaMeta.jobLocation), 45, y);
        y += 8;
      }

      // ── Line Items Table ──
      if (items.length > 0) {
        if (isVendorInvoice) {
          // Vendor invoice: Description | Qty | Unit | Rate | Amount
          autoTable(doc, {
            startY: y,
            head: [['#', 'Description', 'Qty', 'Unit', 'Rate', 'Amount']],
            body: items.map((item: any, idx: number) => [
              String(idx + 1),
              pdfSafe(String(item.description || item.label || '-')).slice(0, 80),
              item.quantity ? String(item.quantity) : '-',
              pdfSafe(item.unit || '-'),
              item.rate > 0 ? fmtMon(item.rate) : '-',
              item.amount > 0 ? fmtMon(item.amount) : '-',
            ]),
            foot: [['', 'TOTAL', '', '', '', fmtMon(totalAmt)]],
            headStyles: { fillColor: [75, 63, 255], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 8, cellPadding: 3 },
            columnStyles: {
              0: { cellWidth: 12, halign: 'center' },
              1: { cellWidth: 85 },
              4: { halign: 'right' },
              5: { halign: 'right' },
            },
          });
        } else {
          // Debris hauler: CLIN / Debris Type table
          autoTable(doc, {
            startY: y,
            head: [['CLIN / Type', 'Description', 'Qty', 'Unit', 'Rate', 'Amount']],
            body: items.map((item: any) => [
              pdfSafe(item.clinCode || item.debrisType || '-'),
              pdfSafe(item.description || item.label || DEBRIS_TYPE_LABELS[item.debrisType as DebrisType] || '-'),
              fmtN(item.quantity || item.invoicedQty || 0),
              pdfSafe(item.unit || 'CY'),
              item.rate || item.transactionRate ? fmtMon(parseFloat(String(item.rate || item.transactionRate))) : '-',
              item.amount || item.invoicedAmount ? fmtMon(parseFloat(String(item.amount || item.invoicedAmount))) : '-',
            ]),
            foot: [['', 'TOTAL', '', '', '', fmtMon(totalAmt)]],
            headStyles: { fillColor: [75, 63, 255], textColor: 255, fontStyle: 'bold' },
            footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 8, cellPadding: 3 },
            columnStyles: { 5: { halign: 'right' } },
          });
        }
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }

      // ── Labor Breakdown (vendor invoices) ──
      if (tampaMeta.laborBreakdown && tampaMeta.laborBreakdown.length > 0) {
        const laborItems = tampaMeta.laborBreakdown.filter((l: any) => l.amount > 0 || l.hours > 0);
        if (laborItems.length > 0) {
          if (y > 240) { doc.addPage(); y = 20; }
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Labor Breakdown', 14, y);
          y += 4;
          autoTable(doc, {
            startY: y,
            head: [['Classification', 'Hours', 'Rate', 'Amount']],
            body: laborItems.map((l: any) => [
              pdfSafe(l.classification || '-'),
              l.hours > 0 ? String(l.hours) : '-',
              l.rate > 0 ? fmtMon(l.rate) : '-',
              l.amount > 0 ? fmtMon(l.amount) : '-',
            ]),
            foot: [['SUBTOTAL', '', '', fmtMon(laborItems.reduce((s: number, l: any) => s + (l.amount || 0), 0))]],
            headStyles: { fillColor: [3, 172, 158], textColor: 255 },
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
            styles: { fontSize: 8, cellPadding: 3 },
            columnStyles: { 3: { halign: 'right' } },
          });
          y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
        }
      }

      // ── Materials (vendor invoices) ──
      if (tampaMeta.materials && tampaMeta.materials.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Materials', 14, y);
        y += 4;
        autoTable(doc, {
          startY: y,
          head: [['Description', 'Supplier Invoice #', 'Amount']],
          body: tampaMeta.materials.map((m: any) => [
            pdfSafe(String(m.description || '-')).slice(0, 90),
            pdfSafe(m.supplierInvoice || '-'),
            m.amount > 0 ? fmtMon(m.amount) : '-',
          ]),
          foot: [['SUBTOTAL', '', fmtMon(tampaMeta.materials.reduce((s: number, m: any) => s + (m.amount || 0), 0))]],
          headStyles: { fillColor: [205, 79, 9], textColor: 255 },
          footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 3 },
          columnStyles: { 2: { halign: 'right' } },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }

      // ── AI Quality Assessment ──
      if (tampaMeta.qualityScore) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Quality Assessment', 14, y);
        y += 6;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Score: ${tampaMeta.qualityScore.toUpperCase()}`, 14, y);
        y += 5;
        if (tampaMeta.aiSummary) {
          const sumLines = doc.splitTextToSize(pdfSafe(tampaMeta.aiSummary), 180);
          doc.text(sumLines, 14, y);
          y += sumLines.length * 4 + 3;
        }
        if (tampaMeta.missingItems && tampaMeta.missingItems.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Missing for FEMA compliance:', 14, y);
          y += 5;
          doc.setFont('helvetica', 'normal');
          tampaMeta.missingItems.slice(0, 10).forEach((item: string) => {
            if (y > 280) { doc.addPage(); y = 20; }
            const lines = doc.splitTextToSize(`• ${pdfSafe(item)}`, 175);
            doc.text(lines, 16, y);
            y += lines.length * 4 + 1;
          });
        }
      }

      // ── Footer ──
      const pageCount = doc.internal.pages.length - 1;
      const pageW = doc.internal.pageSize.getWidth();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`City of Tampa — OpenGov EMC — Page ${i} of ${pageCount} — Generated ${new Date().toLocaleDateString()}`, pageW / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
        doc.setTextColor(0);
      }

      doc.save(`Invoice-${inv.invoiceNumber || inv.id}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      notifications.show({ message: `PDF generation failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
    }
  };

  const fmtMoney = (v: string | number) => {
    const n = typeof v === 'number' ? v : parseFloat(v || '0');
    return n > 0 ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  };

  // Format numbers for editable input display (commas, fixed decimals, no $ prefix)
  const fmtInputNum = (v: string | number | undefined, decimals = 2) => {
    if (v === undefined || v === '' || v === null) return '';
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  const stripNumFmt = (s: string) => s.replace(/[$,]/g, '');

  return (
    <div>
      <input type="file" ref={bulkFileRef} accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
        onChange={handleBulkFile} />

      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Invoices</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={() => downloadWorkbook(generateInvoiceTemplate(), 'invoice-import-template.xlsx')}>
            Download Template
          </Button>
          {canEdit && <Button size="xs" variant="light" leftSection={<IconUpload size={14} />}
            onClick={() => bulkFileRef.current?.click()}>Bulk Import</Button>}
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingInvoices} onClick={() => loadInvoices()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Invoice</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search invoice #, contractor..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Tab switcher */}
      <Group gap={4} mb="md">
        {([
          { id: 'invoices', label: 'Prime Invoices' },
          { id: 'sub_invoices', label: `Sub-Contractor Invoices (${subInvoices.filter(s => s.incidentId === incidentId).length})` },
        ] as const).map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: '6px 16px', borderRadius: 20, border: '1px solid',
              borderColor: activeTab === tab.id ? '#4B3FFF' : '#e2e8f0',
              background: activeTab === tab.id ? '#4B3FFF' : '#fff',
              color: activeTab === tab.id ? '#fff' : '#64748b',
              cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}>
            {tab.label}
          </button>
        ))}
      </Group>

      {selectedIds.size > 0 && (
        <Paper p="sm" radius="md" mb="sm" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
          <Group justify="space-between">
            <Group gap="sm">
              <Badge size="md" color="var(--color-primary-700)">{selectedIds.size} selected</Badge>
              <Text size="sm" c="#4B3FFF" fw={500}>
                {(() => {
                  const sel = iInvoices.filter(i => selectedIds.has(i.id));
                  const total = sel.reduce((s, i) => s + parseFloat(i.totalAmount || '0'), 0);
                  return `Total: ${fmtMoney(total)}`;
                })()}
              </Text>
            </Group>
            <Group gap="sm">
              {selectedIds.size >= 2 && (
                <Button size="xs" color="var(--color-primary-700)"
                  leftSection={<IconGitMerge size={14} />}
                  loading={consolidating}
                  onClick={handleConsolidate}>
                  Consolidate Selected
                </Button>
              )}
              <Button size="xs" variant="subtle" onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {activeTab === 'invoices' && <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '6px 8px', width: 32, position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                  <input type="checkbox"
                    checked={selectedIds.size === filtered.length && filtered.length > 0}
                    onChange={e => {
                      if (e.target.checked) setSelectedIds(new Set(filtered.map(i => i.id)));
                      else setSelectedIds(new Set());
                    }}
                    style={{ width: 15, height: 15, accentColor: '#4B3FFF', cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', whiteSpace: 'nowrap', width: 100 }}>Invoice #</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', width: 180 }}>Contractor</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', whiteSpace: 'nowrap', width: 100 }}>Period</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', whiteSpace: 'nowrap', width: 120 }}>Amount</th>
                <th style={{ padding: '6px 8px', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', whiteSpace: 'nowrap', width: 90 }}>Substantiated</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', width: 290 }}>Status</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingInvoices ? 'Loading...' : 'No invoices found'}
                </td></tr>
              )}
              {filtered.map(inv => {
                // Decode Tampa meta from notes
                const rawNotes = inv.notes ?? '';
                const meta = decodeTampaMeta(rawNotes);
                const contractorAmt = meta.contractorSubmittedAmount ?? parseFloat(inv.totalAmount || '0');
                const monitorAmt = meta.monitorVerifiedAmount ?? 0;
                const cityAmt = parseFloat(inv.totalAmount || '0');
                // Quality score — prefer computed from QC checks, fall back to AI qualityScore field
                const aiMeta2 = decodeFullTampaMeta(rawNotes);
                const qcMeta = meta.qualityChecks ?? aiMeta2.qualityChecks;
                const hasQC = qcMeta && Object.values(qcMeta).some(Boolean);
                const qcScore = hasQC
                  ? computeQualityScore(qcMeta as Record<string, boolean>)
                  : aiMeta2.qualityScore
                    ? { score: aiMeta2.qualityScore, missingItems: aiMeta2.missingItems ?? [] }
                    : null;
                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                    onClick={() => openView(inv)}>
                    <td style={{ padding: '6px 8px' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selectedIds.has(inv.id)}
                        onChange={e => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(inv.id);
                            else next.delete(inv.id);
                            return next;
                          });
                        }}
                        style={{ width: 15, height: 15, accentColor: '#4B3FFF', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '6px 8px', fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={inv.invoiceNumber || `#${inv.id}`}>{inv.invoiceNumber || `#${inv.id}`}</td>
                    <td style={{ padding: '6px 8px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.contractorId ? contractorName(inv.contractorId) : '-'}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#64748b', fontSize: 12 }}>
                      {fmtDate(inv.periodStart)}{inv.periodStart !== inv.periodEnd ? ` — ${fmtDate(inv.periodEnd)}` : ''}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {fmtMoney(cityAmt)}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {(() => {
                        let items: any[] = [];
                        try { items = JSON.parse(inv.lineItems || '[]'); } catch { /* */ }
                        const total = items.reduce((s: number, i: any) => s + (parseFloat(String(i.amount || '0')) || 0), 0);
                        if (total === 0 || cityAmt === 0) return <Text size="xs" c="dimmed">—</Text>;
                        const pct = Math.round((total / cityAmt) * 100);
                        const color = pct > 105 ? '#7C3AED' : pct >= 95 ? 'var(--color-success)' : pct >= 85 ? '#C68700' : 'var(--color-error)';
                        return (
                          <span style={{ fontSize: 12, fontWeight: 700, color, fontFamily: 'monospace' }} title={`${fmtMoney(total)} of ${fmtMoney(cityAmt)}`}>
                            {pct}%
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <div style={{ width: 80, flexShrink: 0 }}>
                          <Badge size="sm" color={STATUS_COLORS[inv.status] ?? '#939598'}>
                            {inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : 'Unknown'}
                          </Badge>
                        </div>
                        <div style={{ width: 55, flexShrink: 0 }}>
                          {(() => {
                            const ps = getPaymentStatus(inv, getPayments);
                            const cfg = PAYMENT_STATUS_COLORS[ps];
                            return (
                              <Badge size="xs" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                                {cfg.label.charAt(0) + cfg.label.slice(1).toLowerCase()}
                              </Badge>
                            );
                          })()}
                        </div>
                        <div style={{ width: 70, flexShrink: 0 }}>
                          {qcScore ? (
                            <Badge size="xs" variant="light"
                              color={qcScore.score === 'complete' ? 'var(--color-success)' : qcScore.score === 'needs_revision' ? '#C68700' : 'var(--color-error)'}
                              title={qcScore.score === 'complete' ? 'Quality checks passed' : `Missing: ${qcScore.missingItems.join(', ')}`}>
                              {qcScore.score === 'complete' ? 'QC Pass' : qcScore.score === 'needs_revision' ? 'QC Review' : 'QC Fail'}
                            </Badge>
                          ) : null}
                        </div>
                        <div style={{ width: 65, flexShrink: 0 }}>
                          {autoCreatedContractorIds.has(inv.contractorId) ? (
                            <Badge size="xs" variant="light" color="#C68700"
                              title="Contractor was auto-created by Package Processor — verify in Contractors tab">
                              Unverified
                            </Badge>
                          ) : null}
                        </div>
                        {(() => {
                          const invMeta = decodeTampaMeta(inv.notes ?? '');
                          if (invMeta.consolidatedInto) {
                            const parent = iInvoices.find(i => i.id === invMeta.consolidatedInto);
                            return (
                              <Badge size="xs" variant="light" color="#6366f1"
                                title={`Consolidated into ${parent?.invoiceNumber || '#' + invMeta.consolidatedInto}`}>
                                → {parent?.invoiceNumber || '#' + invMeta.consolidatedInto}
                              </Badge>
                            );
                          }
                          if (invMeta.sourceInvoices && invMeta.sourceInvoices.length > 0) {
                            return (
                              <Badge size="xs" variant="light" color="#6366f1">
                                {invMeta.sourceInvoices.length} merged
                              </Badge>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)"
                          onClick={() => openView(inv)} title="View Invoice">
                          <IconEye size={14} />
                        </ActionIcon>
                        <div style={{ width: 85, flexShrink: 0, textAlign: 'center' }}>
                          {canEdit && (inv.status === 'submitted' || inv.status === 'draft') ? (
                            <Button size="compact-xs" variant="light" color="#3b82f6" fullWidth
                              leftSection={<IconScale size={12} />}
                              onClick={() => navigate('/debris/reconciliation')}>
                              Reconcile
                            </Button>
                          ) : <span />}
                        </div>
                        {canEdit && inv.status === 'submitted' && (
                          <Button size="compact-xs" variant="light" color="var(--color-success)"
                            onClick={() => handleApprove(inv)}>Approve</Button>
                        )}
                        <div style={{ width: 60, flexShrink: 0, textAlign: 'center' }}>
                          {canEdit && inv.status !== 'consolidated' ? (
                            <Button size="compact-xs" variant="light" color="#03AC9E" fullWidth
                              leftSection={<IconCash size={12} />}
                              onClick={() => { setSelectedInvoice(inv); setPaymentOpen(true); }}>
                              Pay
                            </Button>
                          ) : <span />}
                        </div>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                          onClick={() => generatePDF(inv)} title="Download PDF">
                          <IconFileTypePdf size={14} />
                        </ActionIcon>
                        {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(inv)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(inv)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>}

      {/* ── Sub-Contractor Invoices Tab ─────────────────────── */}
      {activeTab === 'sub_invoices' && (
        <Stack gap={12}>
          <Group justify="space-between">
            <Title order={4}>Sub-Contractor Invoices</Title>
            {canEdit && (
              <Button size="sm" leftSection={<IconPlus size={16} />}
                onClick={() => { setEditSubInv(null); setSubInvForm({ primeContractorId: '', subName: '', subInvoiceNumber: '', subInvoiceDate: '', subAmount: '', workType: '', notes: '' }); setSubInvModal(true); }}>
                Add Sub Invoice
              </Button>
            )}
          </Group>
          <Paper withBorder p={0} style={{ borderRadius: 8 }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                  <tr>
                    {['Sub Name', 'Invoice #', 'Date', 'Amount', 'Work Type', 'Prime Contractor', 'Doc'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                    {canEdit && <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {subInvoices.filter(s => s.incidentId === incidentId).length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No sub-contractor invoices yet. Add them to track what subs billed the prime contractor.</td></tr>
                  )}
                  {subInvoices.filter(s => s.incidentId === incidentId).map(sub => (
                    <tr key={sub.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{sub.subName || '-'}</td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{sub.subInvoiceNumber || '-'}</td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>{sub.subInvoiceDate ? sub.subInvoiceDate.slice(0, 10) : '-'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{sub.subAmount > 0 ? `$${sub.subAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}</td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>{sub.workType || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{contractorName(sub.primeContractorId)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {sub.documentId ? (
                          <Badge size="xs" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>PDF</Badge>
                        ) : (
                          <Badge size="xs" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>Missing</Badge>
                        )}
                      </td>
                      {canEdit && (
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Group gap={4} style={{ justifyContent: 'flex-end' }}>
                            <Button size="xs" variant="subtle" leftSection={<IconPencil size={13} />}
                              onClick={() => {
                                setEditSubInv(sub);
                                setSubInvForm({ primeContractorId: sub.primeContractorId, subName: sub.subName, subInvoiceNumber: sub.subInvoiceNumber, subInvoiceDate: sub.subInvoiceDate, subAmount: String(sub.subAmount), workType: sub.workType, notes: '' });
                                setSubInvModal(true);
                              }}>Edit</Button>
                            <Button size="xs" variant="subtle" color="red" leftSection={<IconTrash size={13} />}
                              onClick={() => { if (confirm('Delete sub invoice?')) deleteSubInvoice(sub.id); }}>Delete</Button>
                          </Group>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </Stack>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Invoice Detail View Modal                             */}
      {/* ═══════════════════════════════════════════════════════ */}
      <Modal opened={viewOpen && !!viewInvoice} onClose={() => { setViewOpen(false); setViewInvoice(null); }}
        title="" fullScreen>
        {viewInvoice && (() => {
          const inv = viewInvoice;
          const rawNotes = inv.notes ?? '';
          const meta = decodeTampaMeta(rawNotes);
          const aiMeta = decodeFullTampaMeta(rawNotes);
          const cityAmt = parseFloat(inv.totalAmount || '0');
          const contractorAmt = meta.contractorSubmittedAmount ?? cityAmt;
          const monitorAmt = meta.monitorVerifiedAmount ?? 0;

          // Parse line items from stored JSON
          let viewLineItems: { description?: string; quantity?: number; unit?: string; rate?: number; amount?: number; debrisType?: string; label?: string; ticketCount?: number; tier?: string; lineItemCode?: string }[] = [];
          try { viewLineItems = JSON.parse(inv.lineItems || '[]'); } catch { /* */ }

          // Use AI extraction line items if no stored line items
          if (viewLineItems.length === 0 && aiMeta.aiSummary) {
            // Fall back to any data from AI extraction -- lineItems may not be in TAMPA meta
            // but the invoice's lineItems field should have them from PackageProcessor
          }

          // Labor breakdown from TAMPA meta
          const laborBreakdown = aiMeta.laborBreakdown || [];
          const laborTotal = laborBreakdown.reduce((s, l) => s + (l.amount || 0), 0);

          // Materials from TAMPA meta
          const materials = aiMeta.materials || [];
          const materialsTotal = materials.reduce((s, m) => s + (m.amount || 0), 0);

          // Quality info
          const qcChecks = aiMeta.qualityChecks;
          const hasQC = qcChecks && Object.values(qcChecks).some(Boolean);
          const qcScore = hasQC ? computeQualityScore(qcChecks as Record<string, boolean>) : null;
          const qualityScoreFromAI = aiMeta.qualityScore;
          const effectiveScore = qcScore?.score || qualityScoreFromAI || null;

          // Line items total
          const lineItemsTotal = viewLineItems.reduce((s, i) => s + ((i as any).amount || 0), 0);

          // Sort line items
          const toggleViewSort = (field: string) => {
            if (viewSortField === field) setViewSortDir(d => d === 'asc' ? 'desc' : 'asc');
            else { setViewSortField(field); setViewSortDir('asc'); }
          };

          const sortedLineItems = [...viewLineItems].sort((a, b) => {
            if (!viewSortField) return 0;
            const va = (a as any)[viewSortField] ?? '';
            const vb = (b as any)[viewSortField] ?? '';
            const n = typeof va === 'number' && typeof vb === 'number'
              ? va - vb
              : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
            return viewSortDir === 'asc' ? n : -n;
          });

          const thStyle = (field: string): React.CSSProperties => ({
            padding: '10px 12px',
            textAlign: field === 'description' || field === 'label' ? 'left' : 'right',
            cursor: 'pointer',
            userSelect: 'none',
            color: viewSortField === field ? '#4B3FFF' : '#64748b',
            fontSize: '0.78rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '2px solid #cbd5e1',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background: '#f8fafc',
          });

          const sortIndicator = (field: string) =>
            viewSortField === field
              ? (viewSortDir === 'desc' ? ' \u2193' : ' \u2191')
              : '';

          return (
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 8px' }}>
              {/* ── HEADER ────────────────────────────────── */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                flexWrap: 'wrap', gap: 16, marginBottom: 24,
              }}>
                <div style={{ flex: '1 1 auto' }}>
                  <Group gap="sm" mb={4}>
                    <Text size="xs" c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      Invoice
                    </Text>
                    <Badge size="sm" color={STATUS_COLORS[inv.status] ?? '#939598'}>
                      {inv.status}
                    </Badge>
                    {effectiveScore && (
                      <Badge size="sm" variant="light"
                        color={effectiveScore === 'complete' ? 'var(--color-success)' : effectiveScore === 'needs_revision' ? '#C68700' : 'var(--color-error)'}>
                        {effectiveScore === 'complete' ? 'Quality: Complete' : effectiveScore === 'needs_revision' ? 'Needs Revision' : 'Reject'}
                      </Badge>
                    )}
                    {autoCreatedContractorIds.has(inv.contractorId) && (
                      <Badge size="sm" variant="light" color="#C68700"
                        title="Contractor was auto-created by Package Processor — verify details in the Contractors tab">
                        Unverified Contractor
                      </Badge>
                    )}
                  </Group>
                  <Text size="xl" fw={800} style={{ fontSize: '1.75rem', lineHeight: 1.2 }}>
                    {inv.invoiceNumber || `#${inv.id}`}
                  </Text>
                  {(aiMeta.vendor || inv.contractorId) && (
                    <Group gap="xs" mt={8}>
                      <IconBuilding size={16} color="#64748b" />
                      <Text size="md" fw={600} c="#1e293b">
                        {aiMeta.vendor || contractorName(inv.contractorId)}
                      </Text>
                    </Group>
                  )}
                  {aiMeta.vendorAddress && (
                    <Text size="sm" c="dimmed" ml={24}>{aiMeta.vendorAddress}</Text>
                  )}
                  <Group gap="md" mt={8}>
                    {inv.periodStart && (
                      <Text size="sm" c="dimmed">
                        {fmtDate(inv.periodStart)}{inv.periodEnd ? ` — ${fmtDate(inv.periodEnd)}` : ''}
                      </Text>
                    )}
                    {aiMeta.poNumber && (
                      <Group gap={4}>
                        <Text size="xs" c="dimmed">PO:</Text>
                        <Text size="sm" fw={500}>{aiMeta.poNumber}</Text>
                      </Group>
                    )}
                  </Group>
                </div>
                <div style={{ textAlign: 'right', minWidth: 180 }}>
                  <Text size="xs" c="dimmed" mb={2}>Amount Paid by City</Text>
                  <Text fw={800} style={{ fontSize: '2rem', color: '#1e293b', fontFamily: 'monospace', lineHeight: 1 }}>
                    {fmtMoney(cityAmt)}
                  </Text>
                  <Group gap="xs" mt={12} justify="flex-end">
                    <Button size="xs" variant="light"
                      leftSection={<IconFileTypePdf size={14} />}
                      onClick={() => generatePDF(inv)}>
                      Download PDF
                    </Button>
                    <Button size="xs" variant="light" color="#3b82f6"
                      leftSection={<IconExternalLink size={14} />}
                      onClick={() => navigate('/debris/evidence')}>
                      Documentation Archive
                    </Button>
                  </Group>
                </div>
              </div>

              {/* ── TAB BAR ───────────────────────────────── */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 }}>
                {([
                  { id: 'details' as const, label: 'Details' },
                  { id: 'documentation' as const, label: 'Documentation' },
                  { id: 'evidence' as const, label: 'Supporting Docs' },
                ]).map(tab => (
                  <button key={tab.id} onClick={() => { setViewDocTab(tab.id); if (tab.id === 'evidence') loadEvidenceDocs(); }}
                    style={{
                      padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
                      fontSize: 14, fontWeight: 600, marginBottom: -2,
                      borderBottom: viewDocTab === tab.id ? '2px solid #4B3FFF' : '2px solid transparent',
                      color: viewDocTab === tab.id ? '#4B3FFF' : '#64748b',
                    }}>
                    {tab.label}
                    {tab.id === 'documentation' && checklistInvoiceType && (() => {
                      const tpl = checklistTemplate ?? DEFAULT_TEMPLATES.find(t => t.invoiceType === checklistInvoiceType);
                      const required = tpl?.items.filter(i => i.required) ?? [];
                      const done = required.filter(i => {
                        if (i.evidenceType === 'auto') {
                          // For tab badge: auto items count as done if they have a satisfied checklist item
                          // (full auto-check computation isn't available here, but file-linked items are)
                          const ci = checklistItems.find(c => c.templateItemId === i.id);
                          return ci && (ci.status === 'satisfied' || ci.status === 'waived');
                        }
                        const ci = checklistItems.find(c => c.templateItemId === i.id);
                        return ci && (ci.status === 'satisfied' || ci.status === 'waived');
                      });
                      return required.length > 0
                        ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: done.length === required.length ? '#dcfce7' : '#fff7ed', color: done.length === required.length ? '#166534' : '#c2410c' }}>{done.length}/{required.length}</span>
                        : null;
                    })()}
                  </button>
                ))}
              </div>

              {/* ── DETAILS TAB CONTENT ───────────────────── */}
              {viewDocTab === 'details' && <>

              {/* ── RECONCILIATION / SUBSTANTIATION PANEL ─── */}
              {(() => {
                const subTotal = viewSubInvoices.reduce((s, si) => s + si.amount, 0);
                const ticketsTotal = lineItemsTotal;
                const substantiated = ticketsTotal + subTotal;
                const gap = cityAmt - substantiated;
                const pct = cityAmt > 0 ? Math.round((substantiated / cityAmt) * 100) : 0;
                const gapColor = gap <= 0 ? 'var(--color-success)' : gap / cityAmt > 0.15 ? 'var(--color-error)' : '#C68700';
                const pctColor = pct >= 95 ? 'var(--color-success)' : pct >= 85 ? '#C68700' : 'var(--color-error)';
                return (
                  <Paper withBorder mb="md" radius="md" style={{ overflow: 'hidden' }}>
                    {/* Header bar */}
                    <div style={{ background: '#1e293b', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Text size="sm" fw={700} style={{ color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: 11 }}>
                        Substantiation
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 120, height: 6, background: '#334155', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pctColor, borderRadius: 3, transition: 'width 0.4s' }} />
                        </div>
                        <Text size="sm" fw={800} style={{ color: pctColor, fontFamily: 'monospace', minWidth: 40 }}>{pct}%</Text>
                      </div>
                    </div>
                    {/* Row of slots */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderTop: '1px solid #e2e8f0' }}>
                      {[
                        { label: 'Invoice Total', sublabel: 'Amount paid by City', value: cityAmt, color: '#1e293b', bold: true },
                        { label: 'Load Tickets', sublabel: `${viewLineItems.length} line item${viewLineItems.length !== 1 ? 's' : ''}`, value: ticketsTotal, color: '#0285C4' },
                        { label: 'Sub-Invoices', sublabel: `${viewSubInvoices.length} linked`, value: subTotal, color: '#D97706' },
                        { label: gap > 0 ? 'Gap' : 'Over-substantiated', sublabel: gap > 0 ? 'Unexplained amount' : 'Exceeds invoice total', value: Math.abs(gap), color: gapColor },
                      ].map((slot, i) => (
                        <div key={i} style={{
                          padding: '14px 16px',
                          borderRight: i < 3 ? '1px solid #e2e8f0' : undefined,
                          background: i === 3 && gap > 0 ? `${gapColor}08` : '#fff',
                        }}>
                          <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>{slot.label}</Text>
                          <Text fw={slot.bold ? 800 : 700} ff="monospace" style={{ fontSize: 18, color: slot.color, marginTop: 2 }}>
                            {fmtMoney(slot.value)}
                          </Text>
                          <Text size="xs" c="dimmed" mt={2}>{slot.sublabel}</Text>
                        </div>
                      ))}
                    </div>
                    {/* Three-party row (only if data present) */}
                    {(contractorAmt !== cityAmt && contractorAmt > 0) || monitorAmt > 0 ? (
                      <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '8px 16px', display: 'flex', gap: 24 }}>
                        <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10, alignSelf: 'center' }}>Three-Party Audit:</Text>
                        {contractorAmt > 0 && contractorAmt !== cityAmt && (
                          <div>
                            <Text size="xs" c="dimmed">Contractor submitted</Text>
                            <Text size="sm" fw={600} ff="monospace">{fmtMoney(contractorAmt)}</Text>
                          </div>
                        )}
                        {monitorAmt > 0 && (
                          <div>
                            <Text size="xs" c="dimmed">Monitor verified</Text>
                            <Text size="sm" fw={600} ff="monospace">{fmtMoney(monitorAmt)}</Text>
                          </div>
                        )}
                        {contractorAmt > 0 && contractorAmt !== cityAmt && (
                          <div>
                            <Text size="xs" c="dimmed">City vs. contractor</Text>
                            <Text size="sm" fw={600} ff="monospace" style={{ color: contractorAmt > cityAmt ? 'var(--color-success)' : 'var(--color-error)' }}>
                              {contractorAmt > cityAmt ? '-' : '+'}{fmtMoney(Math.abs(contractorAmt - cityAmt))}
                            </Text>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </Paper>
                );
              })()}

              {/* ── CLIN BREAKDOWN + TICKET COMPLETENESS ─────── */}
              {(() => {
                // Get tickets linked to this invoice — use allTickets from useAllTickets hook
                let linkedIds: number[] = [];
                try { linkedIds = JSON.parse(inv.ticketIds || '[]'); } catch {}
                // Match tickets to invoice by: stored IDs > contractorId > contractorName
                const invContractor = contractors.find(c => String(c.id) === String(inv.contractorId));
                const invContractorName = invContractor?.companyName?.toLowerCase().trim() || '';
                const invTickets = linkedIds.length > 0
                  ? iTickets.filter(t => linkedIds.includes(t.id))
                  : iTickets.filter(t => {
                      // Match by contractor ID or contractor name
                      const td = t as any;
                      const matchById = inv.contractorId && String(t.contractorId) === String(inv.contractorId);
                      const ticketContName = (td.contractorName || '').toLowerCase().trim();
                      const matchByName = invContractorName && ticketContName && (
                        ticketContName.includes(invContractorName) || invContractorName.includes(ticketContName)
                      );
                      if (!matchById && !matchByName) return false;
                      if (t.status === 'voided') return false;
                      // Period filter — only apply if dates span more than 1 day
                      // (single-day periods are likely import artifacts, not real date ranges)
                      if (inv.periodStart && inv.periodEnd) {
                        const pStart = toISODate(inv.periodStart);
                        const pEnd = toISODate(inv.periodEnd);
                        if (pStart && pEnd && pStart !== pEnd) {
                          const tDate = toISODate(td.loadDate || '');
                          if (tDate && tDate < pStart) return false;
                          if (tDate && tDate > pEnd) return false;
                        }
                      }
                      return true;
                    });
                if (invTickets.length === 0) return (
                  <Paper withBorder mb="md" radius="md" p="md">
                    <Text size="sm" fw={600} mb={4}>Ticket Substantiation Detail</Text>
                    <Text size="xs" c="dimmed">
                      No tickets matched this invoice. {iTickets.length} total tickets loaded.
                      {linkedIds.length > 0 ? ` Searching by ${linkedIds.length} linked ticket IDs.` : ` Searching by contractor "${invContractorName || inv.contractorId}" (ID: ${inv.contractorId}).`}
                      {' '}Check that tickets have a matching contractor name or ID.
                    </Text>
                  </Paper>
                );

                // Compare each invoice line item against ticket totals
                let missingClin = 0;
                let missingGps = 0;
                let missingTruck = 0;
                invTickets.forEach(t => {
                  const td = t as any;
                  if (!td.clinCode) missingClin++;
                  if (!td.pickupLat && !td.pickupLng) missingGps++;
                  if (!td.truckNumber) missingTruck++;
                });

                // Build line item comparison: invoice qty/amount vs ticket qty/amount
                const lineComparison = viewLineItems.map((li: any) => {
                  const desc = String(li.description || li.label || '');
                  const liQty = parseFloat(li.quantity || '0') || 0;
                  const liRate = parseFloat(li.rate || '0') || 0;
                  const liAmount = parseFloat(li.amount || '0') || 0;
                  // Extract CLIN code from description e.g. "(1AQ)"
                  const clinMatch = desc.match(/\(([A-Z0-9]+)\)\s*$/);
                  const clinCode = clinMatch?.[1] || '';
                  // Sum ticket CY and cost for matching CLIN
                  let ticketCy = 0;
                  let ticketCost = 0;
                  let ticketCount = 0;
                  invTickets.forEach(t => {
                    const td = t as any;
                    if (clinCode && td.clinCode === clinCode) {
                      ticketCy += parseFloat(td.calculatedCubicYards || '0') || 0;
                      ticketCost += parseFloat(td.extendedCost || '0') || 0;
                      ticketCount++;
                    }
                  });
                  const cyVariance = liQty - ticketCy;
                  const costVariance = liAmount - ticketCost;
                  return { desc, clinCode, liQty, liRate, liAmount, ticketCy, ticketCost, ticketCount, cyVariance, costVariance };
                });
                const ticketTotal = lineComparison.reduce((s, lc) => s + lc.ticketCost, 0);
                const completeCount = invTickets.length - Math.max(missingClin, missingGps, missingTruck);

                return (
                  <Paper withBorder mb="md" radius="md" style={{ overflow: 'hidden' }}>
                    <div style={{ background: '#0f172a', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text size="xs" fw={700} style={{ color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Ticket Substantiation Detail
                      </Text>
                      <Text size="xs" style={{ color: '#94a3b8' }}>{invTickets.length} tickets · {fmtMoney(ticketTotal)} total</Text>
                    </div>

                    {/* CLIN breakdown table */}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 10, textTransform: 'uppercase' }}>CLIN</th>
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 10 }}>Tickets</th>
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 10 }}>Invoiced</th>
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 10 }}>Substantiated</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 10 }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineComparison.map((lc, i) => {
                            const hasTickets = lc.ticketCost > 0;
                            const pct = lc.liAmount > 0 && hasTickets ? Math.round((lc.ticketCost / lc.liAmount) * 100) : 0;
                            const diff = lc.ticketCost - lc.liAmount;
                            let status = '';
                            let statusColor = '#94a3b8';
                            let rowBg = '#fff';
                            if (!hasTickets) {
                              status = 'No matching tickets';
                              statusColor = '#D33423';
                              rowBg = '#fef2f2';
                            } else if (pct >= 98 && pct <= 102) {
                              status = '✓ Matched';
                              statusColor = 'var(--color-success)';
                            } else if (pct > 102) {
                              status = `Over by ${fmtMoney(diff)}`;
                              statusColor = '#7C3AED';
                              rowBg = '#faf5ff';
                            } else {
                              status = `Short ${fmtMoney(Math.abs(diff))}`;
                              statusColor = '#D33423';
                              rowBg = '#fef2f2';
                            }
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: rowBg }}>
                                <td style={{ padding: '6px 12px', fontWeight: 500, fontSize: 12 }}>
                                  {lc.clinCode || '—'}
                                </td>
                                <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{lc.ticketCount || '—'}</td>
                                <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{fmtMoney(lc.liAmount)}</td>
                                <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>
                                  {hasTickets ? fmtMoney(lc.ticketCost) : '—'}
                                  {hasTickets && <span style={{ color: statusColor, marginLeft: 6, fontSize: 11 }}>({pct}%)</span>}
                                </td>
                                <td style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, color: statusColor }}>
                                  {status}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 700, fontSize: 12 }}>Total</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: 12 }}>{invTickets.length}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: 12 }}>
                              {fmtMoney(lineComparison.reduce((s, lc) => s + lc.liAmount, 0))}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', fontSize: 12 }}>
                              {fmtMoney(lineComparison.reduce((s, lc) => s + lc.ticketCost, 0))}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Evidence coverage: which tickets have their load ticket PDF */}
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
                      {(() => {
                        // Build set of ticket numbers that have a load ticket document
                        // Documents store ticket numbers in their notes field
                        const coveredTickets = new Set<string>();
                        documents.forEach(doc => {
                          if (doc.documentType !== 'load_tickets' && doc.documentType !== 'ticket_image') return;
                          const notes = (doc.notes || '').toLowerCase();
                          const title = (doc.title || '').toLowerCase();
                          invTickets.forEach(t => {
                            const tn = ((t as any).ticketNumber || '').toLowerCase();
                            if (tn && (notes.includes(tn) || title.includes(tn))) coveredTickets.add(tn);
                          });
                        });

                        const totalTickets = invTickets.length;
                        const covered = invTickets.filter(t => coveredTickets.has(((t as any).ticketNumber || '').toLowerCase())).length;
                        const missing = totalTickets - covered;
                        const pct = totalTickets > 0 ? Math.round((covered / totalTickets) * 100) : 0;
                        const allCovered = missing === 0;

                        // Build CLIN options for filter
                        const clinSet = new Set<string>();
                        invTickets.forEach(t => { const c = (t as any).clinCode; if (c) clinSet.add(c); });
                        const clinOptions = [...clinSet].sort();

                        // Apply filters
                        let filteredList = invTickets;
                        if (ticketEvidenceFilter === 'missing') filteredList = filteredList.filter(t => !coveredTickets.has(((t as any).ticketNumber || '').toLowerCase()));
                        if (ticketEvidenceFilter === 'covered') filteredList = filteredList.filter(t => coveredTickets.has(((t as any).ticketNumber || '').toLowerCase()));
                        if (ticketClinFilter) filteredList = filteredList.filter(t => (t as any).clinCode === ticketClinFilter);

                        return (
                          <>
                            {/* Summary bar */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                              <Text size="xs" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Load Ticket Evidence:</Text>
                              <div style={{
                                padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                                background: allCovered ? '#dcfce7' : '#fee2e2',
                                color: allCovered ? '#166534' : '#991b1b',
                              }}>
                                {covered} of {totalTickets} have PDFs ({pct}%)
                              </div>
                              {!allCovered && (
                                <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, maxWidth: 160 }}>
                                  <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: pct >= 80 ? '#C68700' : '#D33423' }} />
                                </div>
                              )}
                            </div>

                            {/* Filters */}
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              {[
                                { value: 'all' as const, label: `All (${totalTickets})` },
                                { value: 'missing' as const, label: `Missing PDF (${missing})`, color: '#991b1b' },
                                { value: 'covered' as const, label: `Has PDF (${covered})`, color: '#166534' },
                              ].map(opt => (
                                <button key={opt.value} onClick={() => setTicketEvidenceFilter(opt.value)}
                                  style={{
                                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                    border: ticketEvidenceFilter === opt.value ? '2px solid #4B3FFF' : '1px solid #e2e8f0',
                                    background: ticketEvidenceFilter === opt.value ? '#ede9fe' : '#fff',
                                    color: ticketEvidenceFilter === opt.value ? '#4B3FFF' : opt.color || '#64748b',
                                  }}>
                                  {opt.label}
                                </button>
                              ))}
                              {clinOptions.length > 1 && (
                                <select value={ticketClinFilter}
                                  onChange={e => setTicketClinFilter(e.target.value)}
                                  style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, border: '1px solid #e2e8f0', color: '#64748b' }}>
                                  <option value="">All CLINs</option>
                                  {clinOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              )}
                              <Text size="xs" c="dimmed">{filteredList.length} shown</Text>
                            </div>

                            {/* Ticket list */}
                            {filteredList.length > 0 ? (
                              <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Ticket #</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Date</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>CLIN</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Amount</th>
                                      <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>PDF</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredList.map(t => {
                                      const td = t as any;
                                      const hasPdf = coveredTickets.has((td.ticketNumber || '').toLowerCase());
                                      return (
                                        <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                          onClick={() => window.open(`/debris/ticket/${t.id}`, '_blank')}>
                                          <td style={{ padding: '5px 10px', color: '#4B3FFF', fontWeight: 500 }}>{td.ticketNumber || `#${t.id}`}</td>
                                          <td style={{ padding: '5px 10px', color: '#64748b' }}>{td.loadDate || '—'}</td>
                                          <td style={{ padding: '5px 10px' }}>{td.clinCode || '—'}</td>
                                          <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{parseFloat(td.extendedCost || '0') > 0 ? fmtMoney(parseFloat(td.extendedCost)) : '—'}</td>
                                          <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                                            {hasPdf
                                              ? <span style={{ color: 'var(--color-success)', fontWeight: 700 }}>✓</span>
                                              : <span style={{ color: '#D33423', fontWeight: 700 }}>✗</span>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <Text size="xs" c="dimmed" ta="center" py="sm">No tickets match the current filter.</Text>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </Paper>
                );
              })()}

              {/* ── LINKED SUB-INVOICES ──────────────────────── */}
              {viewSubInvoices.length > 0 && (
                <Paper withBorder mb="md" radius="md" p="md">
                  <Group gap="xs" mb="sm">
                    <IconReceipt size={16} color="#D97706" />
                    <Text size="sm" fw={700}>Sub-Contractor Invoices</Text>
                    <Badge size="xs" variant="light" color="#D97706">{viewSubInvoices.length}</Badge>
                  </Group>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          {['Sub-Contractor', 'Invoice #', 'Date', 'Amount'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Amount' ? 'right' : 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewSubInvoices.map(si => (
                          <tr key={si.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 10px', fontWeight: 500 }}>{si.vendor || '—'}</td>
                            <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}>{si.invoiceNumber || '—'}</td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>{si.date || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMoney(si.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                          <td colSpan={3} style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12 }}>Sub-Invoice Total</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#D97706' }}>
                            {fmtMoney(viewSubInvoices.reduce((s, si) => s + si.amount, 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Paper>
              )}

              {/* ── JOB INFO CARD ─────────────────────────── */}
              {(aiMeta.jobDescription || aiMeta.jobLocation || aiMeta.disasterReference) && (
                <Paper p="md" radius="md" withBorder mb="md" style={{ background: '#f8fafc' }}>
                  <Group gap="xs" mb="sm">
                    <IconFileText size={16} color="#4B3FFF" />
                    <Text size="sm" fw={700}>Job Information</Text>
                  </Group>
                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                    {aiMeta.jobDescription && (
                      <div>
                        <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Job Description
                        </Text>
                        <Text size="sm" mt={2}>{aiMeta.jobDescription}</Text>
                      </div>
                    )}
                    {aiMeta.jobLocation && (
                      <div>
                        <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Job Location
                        </Text>
                        <Group gap={4} mt={2}>
                          <IconMapPin size={14} color="#64748b" />
                          <Text size="sm">{aiMeta.jobLocation}</Text>
                        </Group>
                      </div>
                    )}
                    {aiMeta.disasterReference && (
                      <div>
                        <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Disaster Reference
                        </Text>
                        <Text size="sm" mt={2} fw={500}>{aiMeta.disasterReference}</Text>
                      </div>
                    )}
                  </SimpleGrid>
                </Paper>
              )}

              {/* ── LINE ITEMS TABLE (MAIN FEATURE) ──────── */}
              <Paper p="md" radius="md" withBorder mb="md">
                <Group gap="xs" mb="sm">
                  <IconReceipt size={16} color="#4B3FFF" />
                  <Text size="sm" fw={700}>Line Items</Text>
                  <Badge size="xs" variant="light" color="var(--color-primary-700)">
                    {viewLineItems.length} item{viewLineItems.length !== 1 ? 's' : ''}
                  </Badge>
                </Group>

                {viewLineItems.length === 0 ? (
                  <Paper p="lg" radius="md" style={{ background: '#fafbfc', textAlign: 'center' }}>
                    <Text size="sm" c="dimmed">No line items extracted for this invoice.</Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      Line items are populated when invoices are processed through the Package Processor or calculated from tickets.
                    </Text>
                  </Paper>
                ) : (
                  <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 500px)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle('_idx'), textAlign: 'center', width: 40, cursor: 'default' }}>#</th>
                          <th style={thStyle('description')} onClick={() => toggleViewSort('description')}>
                            Description{sortIndicator('description')}
                          </th>
                          <th style={thStyle('quantity')} onClick={() => toggleViewSort('quantity')}>
                            Qty{sortIndicator('quantity')}
                          </th>
                          <th style={{ ...thStyle('unit'), cursor: 'default' }}>Unit</th>
                          <th style={thStyle('rate')} onClick={() => toggleViewSort('rate')}>
                            Rate{sortIndicator('rate')}
                          </th>
                          <th style={thStyle('amount')} onClick={() => toggleViewSort('amount')}>
                            Amount{sortIndicator('amount')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLineItems.map((item, idx) => {
                          const desc = (item as any).description || (item as any).label || (item as any).debrisType || `Item ${idx + 1}`;
                          const qty = (item as any).quantity ?? (item as any).cubicYards ?? '';
                          const unit = (item as any).unit || '';
                          const rate = (item as any).rate;
                          const amount = (item as any).amount || 0;
                          return (
                            <tr key={idx} style={{
                              borderBottom: '1px solid #f1f5f9',
                              background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                            }}>
                              <td style={{ padding: '10px 12px', textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
                                {idx + 1}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <Text size="sm">{desc}</Text>
                                {(item as any).tier && (
                                  <Text size="xs" c="dimmed">{(item as any).tier}</Text>
                                )}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                                {typeof qty === 'number' ? qty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : qty || '-'}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', color: '#64748b', fontSize: '0.78rem' }}>
                                {unit}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                                {typeof rate === 'number' && rate > 0 ? fmtMoney(rate) : '-'}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                                {fmtMoney(amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #1e293b' }}>
                          <td colSpan={5} style={{ padding: '12px 12px', fontWeight: 700, textAlign: 'right', fontSize: '0.9rem' }}>
                            Total
                          </td>
                          <td style={{ padding: '12px 12px', textAlign: 'right', fontWeight: 800, fontFamily: 'monospace', fontSize: '1rem', color: '#1e293b' }}>
                            {lineItemsTotal > 0 ? fmtMoney(lineItemsTotal) : fmtMoney(cityAmt)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </Paper>

              {/* ── LABOR BREAKDOWN ───────────────────────── */}
              {laborBreakdown.length > 0 && (
                <Paper p="md" radius="md" withBorder mb="md">
                  <Group gap="xs" mb="sm">
                    <IconTool size={16} color="#0d9488" />
                    <Text size="sm" fw={700}>Labor Breakdown</Text>
                    <Badge size="xs" variant="light" color="teal">
                      {laborBreakdown.length} classification{laborBreakdown.length !== 1 ? 's' : ''}
                    </Badge>
                  </Group>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Classification
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Hours
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Rate
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {laborBreakdown.map((l, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 500 }}>{l.classification}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {l.hours?.toLocaleString() ?? '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {l.rate ? fmtMoney(l.rate) : '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                              {fmtMoney(l.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #1e293b' }}>
                          <td colSpan={3} style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>Subtotal</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, fontFamily: 'monospace', color: '#0d9488' }}>
                            {fmtMoney(laborTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Paper>
              )}

              {/* ── MATERIALS ─────────────────────────────── */}
              {materials.length > 0 && (
                <Paper p="md" radius="md" withBorder mb="md">
                  <Group gap="xs" mb="sm">
                    <IconPackage size={16} color="#d97706" />
                    <Text size="sm" fw={700}>Materials</Text>
                    <Badge size="xs" variant="light" color="orange">
                      {materials.length} item{materials.length !== 1 ? 's' : ''}
                    </Badge>
                  </Group>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Description
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Supplier Invoice #
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {materials.map((m, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ padding: '8px 12px' }}>{m.description}</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#64748b' }}>
                              {m.supplierInvoice || '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                              {fmtMoney(m.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #1e293b' }}>
                          <td colSpan={2} style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>Subtotal</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, fontFamily: 'monospace', color: '#d97706' }}>
                            {fmtMoney(materialsTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Paper>
              )}

              {/* ── QUALITY ASSESSMENT ────────────────────── */}
              {(effectiveScore || aiMeta.aiSummary || (aiMeta.missingItems && aiMeta.missingItems.length > 0)) && (
                <Paper p="md" radius="md" withBorder mb="md" style={{
                  background: effectiveScore === 'complete' ? '#f0fdf4' : effectiveScore === 'reject' ? '#fef2f2' : '#fffbeb',
                  borderColor: effectiveScore === 'complete' ? '#bbf7d0' : effectiveScore === 'reject' ? '#fecaca' : '#fde68a',
                }}>
                  <Group gap="xs" mb="sm">
                    <IconShieldCheck size={16} color={
                      effectiveScore === 'complete' ? '#037730' : effectiveScore === 'reject' ? '#D33423' : '#885604'
                    } />
                    <Text size="sm" fw={700}>Quality Assessment</Text>
                    {effectiveScore && (
                      <Badge size="sm"
                        color={effectiveScore === 'complete' ? 'var(--color-success)' : effectiveScore === 'reject' ? 'var(--color-error)' : '#C68700'}>
                        {effectiveScore === 'complete' ? 'Complete' : effectiveScore === 'needs_revision' ? 'Needs Revision' : 'Reject'}
                      </Badge>
                    )}
                  </Group>

                  {aiMeta.aiSummary && (
                    <Text size="sm" mb="sm" style={{ lineHeight: 1.5 }}>
                      {aiMeta.aiSummary}
                    </Text>
                  )}

                  {/* Compliance indicators */}
                  <Group gap="md" mb={aiMeta.missingItems && aiMeta.missingItems.length > 0 ? 'sm' : 0}>
                    {aiMeta.hasItemizedMaterials !== undefined && (
                      <Group gap={4}>
                        {aiMeta.hasItemizedMaterials
                          ? <IconCheck size={14} color="#037730" />
                          : <IconX size={14} color="#D33423" />}
                        <Text size="xs" c={aiMeta.hasItemizedMaterials ? 'var(--color-success)' : 'var(--color-error)'}>
                          Itemized Materials
                        </Text>
                      </Group>
                    )}
                    {aiMeta.hasLaborBreakdown !== undefined && (
                      <Group gap={4}>
                        {aiMeta.hasLaborBreakdown
                          ? <IconCheck size={14} color="#037730" />
                          : <IconX size={14} color="#D33423" />}
                        <Text size="xs" c={aiMeta.hasLaborBreakdown ? 'var(--color-success)' : 'var(--color-error)'}>
                          Labor Breakdown
                        </Text>
                      </Group>
                    )}
                    {aiMeta.hasDailyServiceTickets !== undefined && (
                      <Group gap={4}>
                        {aiMeta.hasDailyServiceTickets
                          ? <IconCheck size={14} color="#037730" />
                          : <IconX size={14} color="#D33423" />}
                        <Text size="xs" c={aiMeta.hasDailyServiceTickets ? 'var(--color-success)' : 'var(--color-error)'}>
                          Daily Service Tickets
                        </Text>
                      </Group>
                    )}
                    {aiMeta.poNumber && (
                      <Group gap={4}>
                        <IconCheck size={14} color="#037730" />
                        <Text size="xs" c="var(--color-success)">PO Reference</Text>
                      </Group>
                    )}
                    {aiMeta.disasterReference && (
                      <Group gap={4}>
                        <IconCheck size={14} color="#037730" />
                        <Text size="xs" c="var(--color-success)">Disaster Linkage</Text>
                      </Group>
                    )}
                  </Group>

                  {aiMeta.missingItems && aiMeta.missingItems.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Group gap={4} mb={4}>
                        <IconFlag size={14} color="#D33423" />
                        <Text size="xs" fw={600} c="var(--color-error)">Missing Items:</Text>
                      </Group>
                      {aiMeta.missingItems.map((item, idx) => (
                        <Text key={idx} size="xs" c="#64748b" ml={18}>- {item}</Text>
                      ))}
                    </div>
                  )}
                </Paper>
              )}

              {/* ── PAYMENT SUMMARY ──────────── */}
              {meta.paymentRef && (
                <Paper p="md" radius="md" withBorder mb="md" style={{ background: '#f8fafc' }}>
                  <Group gap="xs" mb="sm">
                    <IconCash size={16} color="#03AC9E" />
                    <Text size="sm" fw={700}>Payment Summary</Text>
                  </Group>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Payment Ref</Text>
                    <Text size="sm" fw={500} ff="monospace">{meta.paymentRef}</Text>
                  </div>
                </Paper>
              )}

              {/* ── SOURCE INVOICES (consolidated parent) ─── */}
              {aiMeta.sourceInvoices && (aiMeta.sourceInvoices as number[]).length > 0 && (
                <Paper p="md" radius="md" withBorder mb="md" style={{ background: '#f5f3ff' }}>
                  <Group gap="xs" mb="sm">
                    <IconGitMerge size={16} color="#6366f1" />
                    <Text size="sm" fw={700} c="#6366f1">Consolidated From {(aiMeta.sourceInvoices as number[]).length} Invoices</Text>
                  </Group>
                  <Stack gap={6}>
                    {(aiMeta.sourceInvoices as number[]).map(srcId => {
                      const src = iInvoices.find(i => i.id === srcId);
                      return (
                        <Group key={srcId} gap="sm" style={{ fontSize: '0.85rem' }}>
                          <Badge size="xs" color="#6366f1" variant="light">#{srcId}</Badge>
                          <Text size="sm" fw={500}>{src?.invoiceNumber || `Invoice #${srcId}`}</Text>
                          {src && <Text size="sm" c="dimmed">{contractorName(src.contractorId)}</Text>}
                          {src && <Text size="sm" ff="monospace">{fmtMoney(parseFloat(src.totalAmount || '0'))}</Text>}
                          <Badge size="xs" color={STATUS_COLORS[src?.status ?? 'consolidated'] ?? '#6366f1'}>{src?.status ?? 'consolidated'}</Badge>
                        </Group>
                      );
                    })}
                  </Stack>
                  <Text size="xs" c="dimmed" mt="sm">
                    Combined total: {fmtMoney((aiMeta.sourceInvoices as number[]).reduce((s, id) => {
                      const src = iInvoices.find(i => i.id === id);
                      return s + parseFloat(src?.totalAmount || '0');
                    }, 0))}
                  </Text>
                </Paper>
              )}

              {/* ── CONSOLIDATED INTO (source invoice) ───── */}
              {(aiMeta as any).consolidatedInto && (
                <Paper p="md" radius="md" withBorder mb="md" style={{ background: '#f5f3ff' }}>
                  <Group gap="xs">
                    <IconGitMerge size={16} color="#6366f1" />
                    <Text size="sm" fw={700} c="#6366f1">Consolidated Into</Text>
                    {(() => {
                      const parent = iInvoices.find(i => i.id === (aiMeta as any).consolidatedInto);
                      return (
                        <Group gap="sm">
                          <Text size="sm" fw={500}>{parent?.invoiceNumber || `Invoice #${(aiMeta as any).consolidatedInto}`}</Text>
                          {parent && <Text size="sm" ff="monospace">{fmtMoney(parseFloat(parent.totalAmount || '0'))}</Text>}
                        </Group>
                      );
                    })()}
                  </Group>
                  <Text size="xs" c="dimmed" mt={4}>
                    This invoice has been merged into a consolidated invoice. Payment is processed through the consolidated invoice.
                  </Text>
                </Paper>
              )}

              {/* ── SUPPORTING DOCUMENTS ─────────────────── */}
              {(() => {
                const docIds: string[] = (aiMeta as any).supportingDocIds || [];
                if (docIds.length === 0) return null;
                return (
                  <Paper p="md" radius="md" withBorder mb="md" style={{ background: '#f8fafc' }}>
                    <Group gap="xs" mb="sm">
                      <IconLink size={16} color="#4B3FFF" />
                      <Text size="sm" fw={700}>Supporting Documents</Text>
                      <Badge size="xs" variant="light" color="var(--color-primary-700)">{docIds.length}</Badge>
                    </Group>
                    <Stack gap={4}>
                      {docIds.map(docId => {
                        const doc = allEvidenceDocs.find(d => d.id === docId);
                        return (
                          <Group key={docId} gap="sm">
                            <IconFileText size={14} color="#64748b" />
                            <Text size="sm">{doc?.title || docId}</Text>
                            {doc?.documentType && <Badge size="xs" variant="outline">{doc.documentType}</Badge>}
                          </Group>
                        );
                      })}
                    </Stack>
                  </Paper>
                );
              })()}

              </>}
              {/* ── DOCUMENTATION TAB CONTENT ─────────────────────────── */}
              {viewDocTab === 'documentation' && (
                <InvoiceDocumentationPanel
                  inv={inv}
                  invoiceType={checklistInvoiceType}
                  checklistTemplate={checklistTemplate}
                  checklistTemplateLoading={checklistTemplateLoading}
                  checklistItems={checklistItems}
                  checklistItemsLoading={checklistItemsLoading}
                  checklistWaiverOpen={checklistWaiverOpen}
                  checklistWaiverText={checklistWaiverText}
                  setChecklistWaiverOpen={setChecklistWaiverOpen}
                  setChecklistWaiverText={setChecklistWaiverText}
                  checklistUploading={checklistUploading}
                  setChecklistUploading={setChecklistUploading}
                  saveItem={saveChecklistItem}
                  loadTemplate={loadChecklistTemplate}
                  updateInvoice={updateInvoice}
                  linkedTickets={(() => {
                    let ids: number[] = [];
                    try { ids = JSON.parse(inv.ticketIds || '[]'); } catch { ids = []; }
                    if (ids.length > 0) return iTickets.filter(t => ids.includes(t.id));
                    // Fallback: contractor + period match for PackageProcessor-imported tickets
                    const cid = String(inv.contractorId || '');
                    const start = inv.periodStart || '';
                    const end = inv.periodEnd || '';
                    if (!cid || !start || !end) return [];
                    return iTickets.filter(t => {
                      if (String(t.contractorId) !== cid) return false;
                      const ld = t.loadDate || '';
                      if (!ld) return false;
                      // loadDate from ADMS is MM/DD/YYYY; period is YYYY-MM-DD
                      const parts = ld.split('/');
                      if (parts.length !== 3) return false;
                      const iso = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
                      return iso >= start && iso <= end;
                    });
                  })()}
                  trucks={trucks}
                  canEdit={canEdit}
                />
              )}

              {/* ── EVIDENCE TAB CONTENT ──────────────────── */}
              {viewDocTab === 'evidence' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Text size="sm" fw={700}>Supporting Documents</Text>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {canEdit && (
                        <label style={{ cursor: 'pointer' }}>
                          <input type="file" style={{ display: 'none' }} multiple
                            onChange={async e => {
                              const files = e.target.files;
                              if (!files || files.length === 0) return;
                              const FDoc = FIELDS.debrisDocuments;
                              for (const file of Array.from(files)) {
                                try {
                                  // Create document record first, then upload file
                                  const docRec = await createRecord<Record<string, unknown>>(TABLES.debrisDocuments, {
                                    [FDoc.incidentId]: incidentId,
                                    [FDoc.title]: file.name,
                                    [FDoc.documentType]: 'supporting_evidence',
                                    [FDoc.uploadedBy]: user?.email || 'unknown',
                                    [FDoc.notes]: `|||INVOICELINK|||{"invoiceId":"${String(inv.id)}"}|||ENDINVOICELINK|||`,
                                  });
                                  const recId = (docRec as any).record_id;
                                  if (recId) {
                                    const { attachmentId } = await uploadFileToV2(file, TABLES.debrisDocuments);
                                    await apiUpdateRecord(TABLES.debrisDocuments, recId, { [FDoc.file]: attachmentId });
                                  }
                                } catch { /* upload may partially succeed */ }
                              }
                              notifications.show({ message: `${files.length} file(s) uploaded`, color: 'var(--color-success)' });
                              e.target.value = '';
                              loadEvidenceDocs();
                            }}
                          />
                          <span style={{
                            fontSize: 12, padding: '4px 12px', borderRadius: 6,
                            border: '1px solid #c7d2fe', background: '#fff',
                            color: '#4B3FFF', cursor: 'pointer', fontWeight: 600,
                          }}>
                            Upload Files
                          </span>
                        </label>
                      )}
                      <button onClick={loadEvidenceDocs}
                        style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer' }}>
                        Refresh
                      </button>
                    </div>
                  </div>

                  {evidenceLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading documents...</div>
                  ) : evidenceDocs.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                      <Text size="sm" c="dimmed">No supporting documents linked to this invoice yet.</Text>
                      {canEdit && <Text size="xs" c="dimmed" mt={4}>Upload files above, or use the Documentation tab and Data Intake to attach documents.</Text>}
                    </div>
                  ) : (
                    <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                            <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: 12 }}>Document</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: 12 }}>Source</th>
                            <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: 12 }}>Uploaded By</th>
                            <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: 12 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evidenceDocs.map(doc => (
                            <tr key={doc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px 10px' }}>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{doc.title}</div>
                                {doc.fileName && doc.fileName !== doc.title && (
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{doc.fileName}</div>
                                )}
                              </td>
                              <td style={{ padding: '10px 10px' }}>
                                <span style={{
                                  fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                                  background: doc.type === 'checklist' ? '#ede9fe' : doc.type === 'sow_work_order' ? '#f5f3ff' : doc.type === 'inspection_report' ? '#ecfdf5' : '#f1f5f9',
                                  color: doc.type === 'checklist' ? '#5b21b6' : doc.type === 'sow_work_order' ? '#6d28d9' : doc.type === 'inspection_report' ? '#047857' : '#64748b',
                                }}>
                                  {doc.type === 'checklist' ? 'Checklist' : doc.type === 'sow_work_order' ? 'SOW' : doc.type === 'inspection_report' ? 'Inspection' : doc.type === 'contractor_invoice' ? 'Invoice' : doc.type === 'supporting_evidence' ? 'Supporting' : doc.type || 'Other'}
                                </span>
                              </td>
                              <td style={{ padding: '10px 10px', fontSize: 12, color: '#64748b' }}>{doc.uploadedBy || '—'}</td>
                              <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                                {doc.fileDocId && (
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <button onClick={() => handleViewEvidenceDoc(doc.fileDocId, doc.fileName || doc.title)}
                                      style={{ fontSize: 12, color: '#4B3FFF', background: 'none', border: '1px solid #c7d2fe', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                                      Preview
                                    </button>
                                    <button onClick={async () => { try { const url = await getV2FileDownloadUrl(doc.fileDocId); window.open(url, '_blank'); } catch { notifications.show({ message: 'Download failed', color: 'var(--color-error)' }); } }}
                                      style={{ fontSize: 12, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
                                      ↓
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── INLINE DOCUMENT PREVIEW ──────────────── */}
              {(previewUrl || previewLoading) && viewDocTab === 'evidence' && (
                <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                  }}>
                    <Text size="sm" fw={600} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {previewTitle}
                    </Text>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {previewUrl && (
                        <button onClick={() => window.open(previewUrl, '_blank')}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer' }}>
                          Open in New Tab
                        </button>
                      )}
                      <button onClick={closePreview}
                        style={{ fontSize: 14, padding: '0 6px', borderRadius: 4, border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', cursor: 'pointer', lineHeight: '20px' }}>
                        ✕
                      </button>
                    </div>
                  </div>
                  {previewLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading preview...</div>
                  ) : previewType === 'image' ? (
                    <div style={{ padding: 16, textAlign: 'center', background: '#fafafa' }}>
                      <img src={previewUrl!} alt={previewTitle}
                        style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 4 }} />
                    </div>
                  ) : (
                    <iframe src={previewUrl!} title={previewTitle}
                      style={{ width: '100%', height: 500, border: 'none' }} />
                  )}
                </div>
              )}

              {/* ── ACTIONS BAR ───────────────────────────── */}
              <Divider mb="md" />
              <Group gap="sm" justify="flex-end" mb="md">
                {canEdit && (
                  <Button size="sm" variant="light"
                    leftSection={<IconPencil size={14} />}
                    onClick={() => { setViewOpen(false); setViewInvoice(null); openEdit(inv); }}>
                    Edit Invoice
                  </Button>
                )}
                <Button size="sm" variant="light"
                  leftSection={<IconFileTypePdf size={14} />}
                  onClick={() => generatePDF(inv)}>
                  Download PDF
                </Button>
                {canEdit && inv.status === 'submitted' && (
                  <Button size="sm" color="var(--color-success)"
                    onClick={() => { handleApprove(inv); setViewOpen(false); setViewInvoice(null); }}>
                    Approve
                  </Button>
                )}
                {canEdit && (inv.status === 'submitted' || inv.status === 'draft') && (
                  <Button size="sm" variant="light" color="#C68700"
                    leftSection={<IconFlag size={14} />}
                    onClick={() => {
                      setViewOpen(false); setViewInvoice(null);
                      openEdit(inv);
                      // Will open edit modal where user can change status
                    }}>
                    Flag for Review
                  </Button>
                )}
              </Group>
            </div>
          );
        })()}
      </Modal>

      {/* Evidence Document Picker */}
      <Modal opened={docPickerOpen} onClose={() => setDocPickerOpen(false)}
        title="Select Supporting Documents" size="lg" centered>
        <Stack>
          <TextInput
            placeholder="Search by title..."
            value={docPickerSearch}
            onChange={e => setDocPickerSearch(e.target.value)}
            leftSection={<IconSearch size={14} />}
          />
          <div style={{ overflowY: 'auto', maxHeight: 400 }}>
            {allEvidenceDocs
              .filter(d => !docPickerSearch || d.title.toLowerCase().includes(docPickerSearch.toLowerCase()) || d.documentType.toLowerCase().includes(docPickerSearch.toLowerCase()))
              .map(doc => {
                const isSelected = selectedDocIds.includes(doc.id);
                return (
                  <div key={doc.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      background: isSelected ? '#f0f4ff' : undefined,
                    }}
                    onClick={() => {
                      setSelectedDocIds(prev =>
                        isSelected ? prev.filter(id => id !== doc.id) : [...prev, doc.id]
                      );
                    }}>
                    <input type="checkbox" checked={isSelected} readOnly
                      style={{ width: 15, height: 15, accentColor: '#4B3FFF' }} />
                    <IconFileText size={14} color="#64748b" />
                    <div style={{ flex: 1 }}>
                      <Text size="sm" fw={isSelected ? 600 : 400}>{doc.title}</Text>
                      {doc.documentType && <Text size="xs" c="dimmed">{doc.documentType}</Text>}
                    </div>
                    {isSelected && <IconCheck size={14} color="#4B3FFF" />}
                  </div>
                );
              })}
            {allEvidenceDocs.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" p="lg">Loading documentation archive...</Text>
            )}
          </div>
          <Group justify="flex-end">
            <Text size="xs" c="dimmed">{selectedDocIds.length} selected</Text>
            <Button size="xs" onClick={() => setDocPickerOpen(false)}>Done</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Invoice' : 'New Invoice'} centered size="xl">
        <Stack>
          {/* Top: invoice #, contractor, dates */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Invoice Number" required value={form.invoiceNumber ?? ''}
              onChange={e => setField('invoiceNumber', e.target.value)} />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
            <TextInput label="Period Start" type="date" value={(form.periodStart || '').slice(0, 10)}
              onChange={e => setField('periodStart', e.currentTarget.value)} />
            <TextInput label="Period End" type="date" value={(form.periodEnd || '').slice(0, 10)}
              onChange={e => setField('periodEnd', e.currentTarget.value)} />
          </SimpleGrid>

          <Divider />
          {/* ── Totals ──────────────────────── */}
          {!form.totalAmount && (
            <div style={{ padding: '8px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 12, color: '#c2410c' }}>
              Enter the amount the City paid or committed to pay — this is the anchor everything else substantiates against.
            </div>
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Amount Paid by City"
              value={form.totalAmount ?? ''}
              onChange={e => setField('totalAmount', stripNumFmt(e.target.value))}
              onBlur={e => { const n = parseFloat(stripNumFmt(e.target.value)); if (!isNaN(n) && n > 0) setField('totalAmount', n.toFixed(2)); }}
              placeholder="0.00"
              leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as TampaInvoiceStatus) ?? 'draft'}
              onChange={v => setField('status', v ?? 'draft')} />
          </SimpleGrid>

          {/* Three-party audit — edit only */}
          {editTarget && (
            <>
              <Divider label="Tampa Three-Party Audit" labelPosition="center" />
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <TextInput label="Contractor Submitted"
                  value={(form as any)._tampaContractorSubmitted ?? ''}
                  onChange={e => setForm(prev => ({ ...prev, _tampaContractorSubmitted: stripNumFmt(e.target.value) } as any))}
                  onBlur={e => { const n = parseFloat(stripNumFmt(e.target.value)); if (!isNaN(n) && n > 0) setForm(prev => ({ ...prev, _tampaContractorSubmitted: n.toFixed(2) } as any)); }}
                  placeholder="0.00"
                  leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
                <TextInput label="Monitor Verified"
                  value={(form as any)._tampaMonitorVerified ?? ''}
                  onChange={e => setForm(prev => ({ ...prev, _tampaMonitorVerified: stripNumFmt(e.target.value) } as any))}
                  onBlur={e => { const n = parseFloat(stripNumFmt(e.target.value)); if (!isNaN(n) && n > 0) setForm(prev => ({ ...prev, _tampaMonitorVerified: n.toFixed(2) } as any)); }}
                  placeholder="0.00"
                  leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
                <TextInput label="Payment Reference" placeholder="Check #, wire ref..."
                  value={(form as any)._tampaPaymentRef ?? ''}
                  onChange={e => setForm(prev => ({ ...prev, _tampaPaymentRef: e.target.value } as any))} />
              </SimpleGrid>
            </>
          )}

          {/* ── Line Items ──────────────────── */}
          <Divider label="Line Items" labelPosition="center" />
          <div>
            {manualLineItems.length > 0 && (
              <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, minWidth: 200 }}>Description</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 12, width: 80 }}>Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, width: 80 }}>Unit</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 12, width: 100 }}>Rate</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#64748b', fontSize: 12, width: 110 }}>Amount</th>
                      <th style={{ width: 30 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {manualLineItems.map((li, idx) => (
                      <tr key={li.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '4px 8px' }}>
                          <input value={li.description}
                            onChange={e => { const u = [...manualLineItems]; u[idx] = { ...u[idx], description: e.target.value }; setManualLineItems(u); }}
                            placeholder="Description"
                            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', fontSize: 13 }} />
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <input value={li.quantity} type="text"
                            onChange={e => {
                              const u = [...manualLineItems];
                              const qty = e.target.value;
                              const amt = (parseFloat(qty) || 0) * (parseFloat(li.rate) || 0);
                              u[idx] = { ...u[idx], quantity: qty, amount: amt > 0 ? amt.toFixed(2) : li.amount };
                              setManualLineItems(u);
                            }}
                            placeholder="0"
                            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <select value={li.unit}
                            onChange={e => { const u = [...manualLineItems]; u[idx] = { ...u[idx], unit: e.target.value }; setManualLineItems(u); }}
                            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', fontSize: 12 }}>
                            <option value="">—</option>
                            <option value="CY">CY</option>
                            <option value="TN">TN</option>
                            <option value="EA">EA</option>
                            <option value="HR">HR</option>
                            <option value="LS">LS</option>
                            <option value="LF">LF</option>
                            <option value="SF">SF</option>
                          </select>
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <input value={li.rate} type="text"
                            onChange={e => {
                              const u = [...manualLineItems];
                              const rate = e.target.value;
                              const amt = (parseFloat(li.quantity) || 0) * (parseFloat(rate) || 0);
                              u[idx] = { ...u[idx], rate, amount: amt > 0 ? amt.toFixed(2) : li.amount };
                              setManualLineItems(u);
                            }}
                            placeholder="0.00"
                            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', fontSize: 13, textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <input value={li.amount} type="text"
                            onChange={e => { const u = [...manualLineItems]; u[idx] = { ...u[idx], amount: e.target.value }; setManualLineItems(u); }}
                            placeholder="0.00"
                            style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', fontSize: 13, textAlign: 'right', fontWeight: 600 }} />
                        </td>
                        <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                          <button onClick={() => setManualLineItems(prev => prev.filter((_, i) => i !== idx))}
                            style={{ background: 'none', border: 'none', color: '#D33423', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                            title="Remove line item">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {manualLineItems.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan={4} style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: 12 }}>Total</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>
                          ${manualLineItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
            <Button size="xs" variant="light" onClick={() => setManualLineItems(prev => [...prev, {
              id: `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              description: '', quantity: '1', unit: '', rate: '', amount: '',
            }])}>
              + Add Line Item
            </Button>
          </div>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Invoice'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Invoice" centered size="sm">
        <Stack>
          <Text size="sm">Delete invoice <strong>{deleteTarget?.invoiceNumber}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Payment Tracking Modal ──────────────────────── */}
      <Modal opened={paymentOpen && !!selectedInvoice} onClose={() => { setPaymentOpen(false); setSelectedInvoice(null); }}
        title={`Payments — ${selectedInvoice?.invoiceNumber ?? ''}`} centered size="lg">
        {selectedInvoice && (() => {
          const inv = selectedInvoice;
          const approvedAmt = parseFloat(inv.totalAmount || '0');
          const payments = getPayments(inv);
          const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
          const outstanding = approvedAmt - totalPaid;

          return (
            <Stack>
              {/* Payment status pipeline */}
              <Paper p="sm" radius="md" withBorder style={{ background: '#f8fafc' }}>
                <Group justify="space-between">
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <Text size="xs" c="dimmed">Invoiced</Text>
                    <Text size="sm" fw={700} c="#3b82f6">{fmtMoney(approvedAmt)}</Text>
                  </div>
                  <Text c="dimmed">&rarr;</Text>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <Text size="xs" c="dimmed">Approved</Text>
                    <Text size="sm" fw={700} c="var(--color-success)">{fmtMoney(approvedAmt)}</Text>
                  </div>
                  <Text c="dimmed">&rarr;</Text>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <Text size="xs" c="dimmed">Paid</Text>
                    <Text size="sm" fw={700} c="#03AC9E">{fmtMoney(totalPaid)}</Text>
                  </div>
                </Group>
              </Paper>

              {/* Outstanding summary */}
              <SimpleGrid cols={{ base: 1, sm: 1 }} spacing="xs">
                <Paper p="xs" radius="md" withBorder style={{ textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Outstanding</Text>
                  <Text size="sm" fw={800} c={outstanding > 0 ? '#D33423' : 'var(--color-success)'}>{fmtMoney(Math.max(0, outstanding))}</Text>
                </Paper>
              </SimpleGrid>

              {/* Payment history */}
              <Text fw={700} size="sm">Payment History</Text>
              {payments.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="sm">No payments recorded yet</Text>
              ) : (
                <Paper withBorder>
                  <div style={{ overflowX: 'auto', maxHeight: 200 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left', position: 'sticky', top: 0, background: '#f8fafc' }}>Date</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right', position: 'sticky', top: 0, background: '#f8fafc' }}>Amount</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left', position: 'sticky', top: 0, background: '#f8fafc' }}>Reference</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left', position: 'sticky', top: 0, background: '#f8fafc' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '6px 10px' }}>{p.date ? (() => { const [y, m, d] = p.date.split('-'); return `${m}/${d}/${y}`; })() : '-'}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMoney(p.amount)}</td>
                            <td style={{ padding: '6px 10px' }}>{p.reference || '-'}</td>
                            <td style={{ padding: '6px 10px' }}><Text size="xs" c="dimmed">{p.notes || '-'}</Text></td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                          <td style={{ padding: '6px 10px' }}>Total</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(totalPaid)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Paper>
              )}

              {/* Add payment form */}
              {canEdit && (
                <>
                  <Divider label="Record Payment" labelPosition="center" />
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                    <TextInput label="Payment Date" type="date" size="xs"
                      value={paymentDate} onChange={e => setPaymentDate(e.currentTarget.value)} required />
                    <TextInput label="Amount" size="xs" placeholder="0.00" type="number" step="0.01" min="0"
                      value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} required
                      leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
                    <TextInput label="Reference (Check #, Wire)" size="xs" placeholder="CHK-44521"
                      value={paymentRef} onChange={e => setPaymentRef(e.target.value)} />
                    <TextInput label="Notes" size="xs" placeholder="Payment notes..."
                      value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} />
                  </SimpleGrid>
                  <Group justify="flex-end">
                    <Button size="xs" onClick={() => addPayment(inv)} leftSection={<IconCash size={14} />}>
                      Record Payment
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Sub Invoice Add/Edit Modal ──────────────────────── */}
      <Modal opened={subInvModal} onClose={() => setSubInvModal(false)} title={editSubInv ? 'Edit Sub Invoice' : 'Add Sub Invoice'} size="lg">
        <Stack gap={12}>
          <Select label="Prime Contractor" data={contractorSelect} value={subInvForm.primeContractorId}
            onChange={(v: string | null) => setSubInvForm(f => ({ ...f, primeContractorId: v || '' }))} required />
          <TextInput label="Subcontractor Name" value={subInvForm.subName}
            onChange={(e: any) => setSubInvForm(f => ({ ...f, subName: e.target.value }))} required />
          <SimpleGrid cols={2} spacing="sm">
            <TextInput label="Invoice Number" value={subInvForm.subInvoiceNumber}
              onChange={(e: any) => setSubInvForm(f => ({ ...f, subInvoiceNumber: e.target.value }))} />
            <TextInput label="Invoice Date" type="date" value={subInvForm.subInvoiceDate}
              onChange={(e: any) => setSubInvForm(f => ({ ...f, subInvoiceDate: e.target.value }))} />
          </SimpleGrid>
          <SimpleGrid cols={2} spacing="sm">
            <TextInput label="Amount" type="number" value={subInvForm.subAmount}
              onChange={(e: any) => setSubInvForm(f => ({ ...f, subAmount: e.target.value }))}
              leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
            <TextInput label="Work Type" placeholder="e.g. debris removal, monitoring"
              value={subInvForm.workType}
              onChange={(e: any) => setSubInvForm(f => ({ ...f, workType: e.target.value }))} />
          </SimpleGrid>
          <Text size="xs" c="dimmed">Upload the actual sub-contractor invoice PDF in the Documentation Archive and link the document ID separately.</Text>
          <Group justify="flex-end" gap={8}>
            <Button variant="light" onClick={() => setSubInvModal(false)}>Cancel</Button>
            <Button loading={savingSubInv} onClick={async () => {
              if (!subInvForm.subName || !subInvForm.primeContractorId) {
                notifications.show({ message: 'Contractor and sub name required', color: '#C68700' });
                return;
              }
              setSavingSubInv(true);
              try {
                if (editSubInv) {
                  await updateSubInvoice(editSubInv.id, {
                    subName: subInvForm.subName, subInvoiceNumber: subInvForm.subInvoiceNumber,
                    subInvoiceDate: subInvForm.subInvoiceDate, subAmount: parseFloat(subInvForm.subAmount) || 0,
                    workType: subInvForm.workType,
                  });
                } else {
                  await createSubInvoice({
                    incidentId, primeContractorId: subInvForm.primeContractorId,
                    subName: subInvForm.subName, subInvoiceNumber: subInvForm.subInvoiceNumber,
                    subInvoiceDate: subInvForm.subInvoiceDate, subAmount: parseFloat(subInvForm.subAmount) || 0,
                    workType: subInvForm.workType,
                  });
                }
                setSubInvModal(false);
                notifications.show({ message: editSubInv ? 'Sub invoice updated' : 'Sub invoice added', color: 'var(--color-success)' });
              } finally {
                setSavingSubInv(false);
              }
            }}>
              {editSubInv ? 'Update' : 'Add'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Bulk Import Modal ── */}
      <Modal opened={bulkOpen} onClose={() => { if (!bulkImporting) setBulkOpen(false); }}
        title="Bulk Import Invoices" centered size="xl">
        <Stack gap={12}>
          {bulkErrors.length > 0 && (
            <Alert color="var(--color-warning)" icon={<IconAlertTriangle size={16} />}>
              {bulkErrors.map((e, i) => <div key={i}>{e}</div>)}
            </Alert>
          )}

          {bulkRows.length === 0 ? (
            <Text size="sm" c="dimmed">No rows found in spreadsheet.</Text>
          ) : (
            <>
              {bulkRows.some(r => r.existingId) && (
                <Checkbox
                  label="Update existing invoices with new data"
                  checked={bulkUpdateExisting}
                  onChange={(e) => setBulkUpdateExisting((e.target as HTMLInputElement).checked)}
                />
              )}

              {(() => {
                const createCount = bulkRows.filter(r => r.valid && !r.existingId).length;
                const updateCount = bulkRows.filter(r => r.valid && !!r.existingId).length;
                const errorCount = bulkRows.filter(r => !r.valid).length;
                return (
                  <Group gap="sm">
                    {createCount > 0 && (
                      <Badge size="md" color="var(--color-success)">
                        {createCount} to create
                      </Badge>
                    )}
                    {updateCount > 0 && (
                      <Badge size="md" color={bulkUpdateExisting ? '#3b82f6' : 'var(--color-warning)'}>
                        {updateCount} {bulkUpdateExisting ? 'to update' : 'existing — skip'}
                      </Badge>
                    )}
                    {errorCount > 0 && (
                      <Badge size="md" color="var(--color-error)">
                        {errorCount} with errors
                      </Badge>
                    )}
                  </Group>
                );
              })()}

              <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 340px)' }}>
                <Table>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                    <tr>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Row</th>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Invoice #</th>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Contractor</th>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600, textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Type</th>
                      <th style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map(row => (
                      <tr key={row.rowNum} style={{ background: !row.valid ? '#fef2f2' : row.existingId && !bulkUpdateExisting ? '#fffbeb' : undefined }}>
                        <td style={{ padding: '6px 10px', fontSize: 13 }}>{row.rowNum}</td>
                        <td style={{ padding: '6px 10px', fontSize: 13, fontWeight: 600 }}>{row.invoiceNumber || '-'}</td>
                        <td style={{ padding: '6px 10px', fontSize: 13 }}>
                          {row.matchedContractorId ? (
                            <span style={{ color: '#037730' }}>
                              <IconCheck size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              {row.matchedContractorName}
                            </span>
                          ) : row.contractorName ? (
                            <span style={{ color: '#4B3FFF' }}>
                              {row.contractorName}
                              <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>+ new</span>
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 10px', fontSize: 13 }}>{row.invoiceDate || '-'}</td>
                        <td style={{ padding: '6px 10px', fontSize: 13, textAlign: 'right' }}>
                          {row.totalAmount > 0 ? `$${row.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        </td>
                        <td style={{ padding: '6px 10px', fontSize: 13 }}>{row.invoiceType || '-'}</td>
                        <td style={{ padding: '6px 10px', fontSize: 13 }}>
                          {!row.valid ? (
                            <span style={{ color: '#D33423', fontSize: 12 }}>
                              {row.errors.join('; ')}
                            </span>
                          ) : row.existingId && bulkUpdateExisting ? (
                            <Badge size="sm" color="#3b82f6">Update</Badge>
                          ) : row.existingId && !bulkUpdateExisting ? (
                            <Badge size="sm" color="var(--color-warning)">Exists — skip</Badge>
                          ) : (
                            <Badge size="sm" color="var(--color-success)">Create</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {(() => {
            const createCount = bulkRows.filter(r => r.valid && !r.existingId).length;
            const updateCount = bulkUpdateExisting ? bulkRows.filter(r => r.valid && !!r.existingId).length : 0;
            const actionCount = createCount + updateCount;
            const label = [
              createCount > 0 ? `${createCount} create` : '',
              updateCount > 0 ? `${updateCount} update` : '',
            ].filter(Boolean).join(', ');
            return (
              <Group justify="flex-end" gap="sm" mt="sm">
                <Button size="sm" variant="light" onClick={() => setBulkOpen(false)}
                  disabled={bulkImporting}>Cancel</Button>
                <Button size="sm" leftSection={<IconUpload size={16} />}
                  loading={bulkImporting}
                  disabled={actionCount === 0}
                  onClick={handleBulkImport}>
                  Import ({label})
                </Button>
              </Group>
            );
          })()}
        </Stack>
      </Modal>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
